import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { money } from "../../lib/api";
import { errorWithTicket, errorToastFromAxios } from "../../lib/supportToast";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetCloseX, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";
import BankInstructionsCard from "../../components/BankInstructionsCard";
import { toast } from "sonner";
import {
  Wallet, CreditCard, ArrowRight, ArrowDownLeft, ArrowUpRight, Plus,
  Building2, Banknote, Check, Lock, Sparkles, Download, TrendingUp,
  Landmark, Zap, Shield, BarChart3, RefreshCw, AlertCircle, FileText, Truck, RotateCcw,
  Activity,
} from "lucide-react";
import PlaidLinkButton, { PlaidConnectionCard } from "../../components/PlaidLinkButton";
import TwoCheckoutModal from "../../components/TwoCheckoutModal";
import StepUpModal from "../../components/StepUpModal";
import WalletActivityPanel from "../../components/WalletActivityPanel";
import BuyerInvoiceDetailDialog from "../../components/BuyerInvoiceDetailDialog";
import InvoiceExpiryChip from "../../components/InvoiceExpiryChip";
import PreCreditPanel from "../../components/PreCreditPanel";
import PaymentsTabBar from "../../components/PaymentsTabBar";
import VerqoAccountCard from "../../components/VerqoAccountCard";
import LimitRequestCard from "../../components/LimitRequestCard";
import { SAMPLE_INVOICES } from "../../lib/sampleData";
import { SamplePreviewBanner, SampleChip, SampleInvoiceDialog } from "../../components/SamplePreview";
import { useSupplier } from "../../context/SupplierContext";

// ─────────────────────────────────────────────────────────────────────────────
// BuyerPayments — Banking-style money portal (section driven by URL hash)
//   /buyer/payments#overview · #wallet · #credit · #methods · #activity
//   The sub-nav lives in the main sidebar (PortalShell) — nested under Payments.
// ─────────────────────────────────────────────────────────────────────────────

const VALID = ["overview", "wallet", "credit", "pre-credit", "methods", "activity"];

// ── Inline helper — Payment success card with 30s undo countdown ────────
// Renders after a successful pay. Counts down from 30 seconds; the user
// can click Undo within the window to reverse the wallet move. When the
// timer hits zero we fire `onTimerExpire` to close the panel + redirect.
function PaymentSuccessWithUndo({ invoice, payState, money, onUndo, onTimerExpire }) {
  const [secondsLeft, setSecondsLeft] = useState(30);
  const [undoing, setUndoing] = useState(false);
  useEffect(() => {
    if (secondsLeft <= 0) { onTimerExpire?.(); return; }
    const t = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, onTimerExpire]);
  const pct = (secondsLeft / 30) * 100;
  const handleUndo = async () => {
    setUndoing(true);
    try { await onUndo(); } finally { setUndoing(false); }
  };
  return (
    <div className="py-8" data-testid="payment-success-state">
      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 inline-flex items-center justify-center mb-5">
          <Check className="w-8 h-8 text-emerald-700" strokeWidth={2.5}/>
        </div>
        <div className="display text-3xl tracking-tight text-foreground">{payState.message}</div>
        <div className="text-[10.5px] mono uppercase tracking-[0.2em] text-foreground/50 mt-3">
          Invoice {invoice.code} · {money(invoice.amount)}
        </div>
        {payState.details && (
          <p className="mt-4 max-w-[360px] text-[13px] text-foreground/65 leading-relaxed">
            {payState.details}
          </p>
        )}
        {/* Primary CTA — proceed to track order without waiting for the
            30s undo timer. Buyers asked for an obvious next-step button. */}
        <Button
          onClick={() => onTimerExpire?.()}
          data-testid="continue-after-pay-btn"
          className="mt-6 w-full max-w-[360px] rounded-full h-11 bg-foreground text-background hover:bg-foreground/90"
        >
          Continue · track your order <ArrowRight className="w-4 h-4 ml-2"/>
        </Button>
        {/* 30-second undo window */}
        <div className="mt-4 w-full max-w-[360px]">
          <div className="rounded-xl border border-border bg-secondary/20 p-3.5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-left">
                <div className="text-[12px] font-medium text-foreground inline-flex items-center gap-1.5">
                  <RotateCcw className="w-3.5 h-3.5"/>Undo within {secondsLeft}s
                </div>
                <div className="text-[11px] text-foreground/55 mt-0.5">Reverses the wallet move instantly.</div>
              </div>
              <Button
                size="sm" variant="outline"
                onClick={handleUndo} disabled={undoing || secondsLeft <= 0}
                className="rounded-full"
                data-testid="undo-payment-btn"
              >
                {undoing ? <><RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5"/>Undoing…</> : "Undo"}
              </Button>
            </div>
            <div className="mt-3 h-1 rounded-full bg-border overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }}/>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-foreground/45">
            Or wait — we'll route you automatically in {secondsLeft}s.
          </p>
        </div>
      </div>
    </div>
  );
}


export default function BuyerPayments() {
  const loc = useLocation();
  const nav = useNavigate();
  const { activeSellerName, scopeToSupplier } = useSupplier();
  const section = useMemo(() => {
    const h = (loc.hash || "").replace("#", "");
    return VALID.includes(h) ? h : "overview";
  }, [loc.hash]);

  const [wallet, setWallet] = useState(null);
  const [methods, setMethods] = useState([]);
  const [cap, setCap] = useState(null);
  const [lenders, setLenders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sellerTerms, setSellerTerms] = useState([]); // seller-granted free credit lines
  const [plaidSummary, setPlaidSummary] = useState(null);
  const [plaidRefreshing, setPlaidRefreshing] = useState(false);

  const [topup, setTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState(5000);
  // Iter 156 · "Top up + pay" — when Add-funds is clicked from the
  // wallet-insufficient state on the Pay drawer, we tag the topup with
  // the invoice the buyer was trying to pay. Backend auto-settles the
  // invoice once the topup webhook fires SUCCESS.
  const [topupPendingInvoice, setTopupPendingInvoice] = useState(null);  // {id, code, amount, shortfall, sellerName}
  // Phase 2c — top-level invoice detail dialog. The OpenBalancesList
  // owns its own copy, but we also wire the paid-history list here so
  // paid invoices show the same breakdown for reconciliation.
  const [paidViewInvoice, setPaidViewInvoice] = useState(null);
  const [topupSource, setTopupSource] = useState("ach");
  const [topupStepUp, setTopupStepUp] = useState(false);
  const [methodModal, setMethodModal] = useState(false);
  const [newCard, setNewCard] = useState({
    type: "card", brand: "visa", last4: "", exp: "", label: "",
    cardholder_name: "",
    billing_address_line1: "", billing_address_line2: "",
    billing_city: "", billing_state: "",
    billing_postal_code: "", billing_country: "US",
    cardholder_authorized: false,
    bank_name: "", account_holder: "", account_type: "checking",
    routing: "", account: "",
    wire_routing: "",
    swift_bic: "", iban: "", currency: "USD", bank_country: "US",
  });
  const [applyOpen, setApplyOpen] = useState(null);
  const [payInv, setPayInv] = useState(null);
  // Payment state machine (Feb 2026, inline UX):
  //   idle       — form visible, user picking method
  //   processing — spinner overlay inside the panel + button locked
  //   success    — green-check confirmation card replaces the form
  //   error      — inline error banner above the form, retry-able
  const [payState, setPayState] = useState({ kind: "idle", message: null, details: null });
  // Feb 2026 — payment SMS step-up state. When user clicks Pay, we first
  // send an OTP to the OWNER's phone, render an OTP entry view, then
  // call /payments/sms-verify, get an approval_token, and pass it to
  // /pay or /authorize.
  //   stepupState:
  //     null            — not yet needed (idle)
  //     "challenging"   — calling /sms-challenge
  //     "awaiting_code" — OTP input rendered
  //     "verifying"     — calling /sms-verify
  //   stepupCtx: { challenge_id, phone_masked, owner_name, sent_to_owner }
  const [stepupState, setStepupState] = useState(null);
  const [stepupCtx,   setStepupCtx]   = useState(null);
  const [stepupCode,  setStepupCode]  = useState("");
  const [stepupErr,   setStepupErr]   = useState(null);
  const [approvalToken, setApprovalToken] = useState(null);
  const [payMode, setPayMode] = useState("now"); // "now" = pay upfront · "authorize" = auto-debit on due date
  const [payMethod, setPayMethod] = useState("wallet");
  const [autoDebit, setAutoDebit] = useState(true);
  const [sellerOfferId, setSellerOfferId] = useState(null); // selected seller offer id when method===seller_terms
  const [extension, setExtension] = useState(null); // null | "net60" | "net90" — buyer-paid term extension

  // ── the lender integration state ──
  const [twoTerms, setTwoTerms] = useState([]);           // [{code,label,days,fee_pct,popular}]
  const [selectedTwoTerm, setSelectedTwoTerm] = useState("net30"); // buyer's chosen Net-X
  const [twoProfile, setTwoProfile] = useState(null);     // { profile, complete }
  const [twoCheckout, setTwoCheckout] = useState(null);   // { paymentUrl, amount, invoiceId }
  const [twoStarting, setTwoStarting] = useState(false);
  const [twoProfileOpen, setTwoProfileOpen] = useState(false);
  const [twoProfileForm, setTwoProfileForm] = useState({
    company_name: "", organization_number: "", first_name: "", last_name: "",
    phone: "", country: "US",
    billing_address: { street_address: "", city: "", postal_code: "", region: "", country: "US" },
  });

  const goSection = (s) => nav(`/buyer/payments#${s}`);

  // ── Detect ?pay=invId in URL — auto-open pay dialog ──
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const id = params.get("pay");
    const mode = params.get("mode");
    if (id) setPayInv(id);
    if (mode === "authorize" || mode === "now") setPayMode(mode);
  }, [loc.search]);

  // If buyer has no credit line AND seller hasn't offered free terms, the
  // "credit" payment method shouldn't be selectable — fall back to wallet.
  useEffect(() => {
    if (cap && cap.credit_limit === 0 && payMethod === "credit") {
      setPayMethod("wallet");
    }
  }, [cap, payMethod]);

  const load = () => Promise.all([
    api.get("/wallet"), api.get("/payment-methods"),
    api.get("/capital/summary"), api.get("/lenders"),
    api.get("/invoices"), api.get("/invoices/summary"),
    api.get("/buyer/seller-terms"),
    api.get("/onboarding/plaid/summary").catch(() => ({ data: null })),
    api.get("/two/terms-options").catch(() => ({ data: null })),
    api.get("/two/profile").catch(() => ({ data: null })),
  ]).then(([w, m, c, l, i, s, st, pl, tt, tp]) => {
    setWallet(w.data); setMethods(m.data); setCap(c.data);
    setLenders(l.data); setInvoices(i.data); setSummary(s.data);
    setSellerTerms(st.data);
    setPlaidSummary(pl.data);
    if (tt?.data?.options) setTwoTerms(tt.data.options);
    if (tp?.data) setTwoProfile(tp.data);
  });
  useEffect(() => { load(); }, []);

  // Supplier-scoped views: invoices and seller-terms filtered to active supplier.
  // Wallet, capital, methods are buyer-owned and not scoped.
  const scopedInvoices = useMemo(() => scopeToSupplier(invoices), [invoices, scopeToSupplier]);
  const scopedSellerTerms = useMemo(() => scopeToSupplier(sellerTerms), [sellerTerms, scopeToSupplier]);

  // Invoice-derived activity rows for the Activity ledger — surfaces
  // "Invoice paid"/"Invoice issued" events next to raw wallet transactions
  // so buyers see the full picture (wallet + AP) in one filterable list.
  const invoiceActivityItems = useMemo(() => {
    return (scopedInvoices || []).slice(0, 100).map(i => ({
      type:    i.status === "paid" ? "invoice_paid" : "invoice_issued",
      title:   i.status === "paid" ? `Paid invoice ${i.code}` : `Invoice ${i.code} issued`,
      memo:    `${i.seller_name || ""}${i.due_date ? ` · due ${new Date(i.due_date).toLocaleDateString()}` : ""}`,
      method:  i.terms || "",
      ref:     i.code,
      amount:  -Math.abs(Number(i.amount || 0)),
      created_at: i.paid_at || i.created_at,
    }));
  }, [scopedInvoices]);

  // Compute money in/out from wallet transactions (last 30d)
  const flow = useMemo(() => {
    if (!wallet) return { inflow: 0, outflow: 0, net: 0 };
    const cutoff = Date.now() - 30 * 86400000;
    let inflow = 0, outflow = 0;
    wallet.transactions.forEach(tx => {
      if (new Date(tx.created_at).getTime() < cutoff) return;
      if (tx.amount > 0) inflow += tx.amount;
      else outflow += Math.abs(tx.amount);
    });
    return { inflow, outflow, net: inflow - outflow };
  }, [wallet]);

  // Find an active seller offer for the currently-open invoice's seller
  const payInvoice = invoices.find(i => i.id === payInv);
  const eligibleSellerOffer = useMemo(() => {
    if (!payInvoice) return null;
    return sellerTerms.find(o =>
      o.status === "active" &&
      o.seller_id === payInvoice.seller_id &&
      o.available >= payInvoice.amount,
    ) || null;
  }, [payInvoice, sellerTerms]);

  // Auto-select seller free terms when invoice opens and an offer covers it.
  // EXCEPT when the buyer arrived with `?mode=now` (clicked "Pay upfront")
  // from Orders/Invoices — in that case we keep payMethod=wallet so the
  // Prepay rail (Wallet/ACH/Wire/Card) is selected by default and the
  // seller-terms tile becomes an optional alternative (still visible).
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const arrivedPrepay = params.get("mode") === "now";
    if (eligibleSellerOffer && payInv && !arrivedPrepay) {
      setPayMethod("seller_terms");
      setSellerOfferId(eligibleSellerOffer.id);
    } else if (eligibleSellerOffer && payInv && arrivedPrepay) {
      // Prepay arrival → default to wallet but remember the offer id so the
      // user can switch to seller_terms with one click.
      setSellerOfferId(eligibleSellerOffer.id);
      if (payMethod === "seller_terms") setPayMethod("wallet");
    } else {
      setSellerOfferId(null);
      setExtension(null);
      if (payMethod === "seller_terms") setPayMethod("wallet");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payInv, eligibleSellerOffer?.id, loc.search]);

  if (!wallet || !cap || !summary) return <div className="p-12 text-foreground/40">Loading…</div>;

  // ── Actions ──
  const doTopup = async (stepUpToken) => {
    try {
      // Bank-rail topups go through the shared /wallet/topup endpoint, which
      // resolves to whichever rail BANK_RAIL selects. Wyzia was removed in
      // Jun 2026; Shield is the active rail and has no RFP, so that option
      // degrades to same-day ACH server-side.
      const url  = "/wallet/topup";
      const body = { amount: Number(topupAmount), method_type: topupSource,
        ...(topupPendingInvoice?.id ? { pending_invoice_id: topupPendingInvoice.id } : {}) };
      const r = await api.post(
        url, body,
        stepUpToken ? { headers: { "X-Step-Up-Token": stepUpToken } } : undefined,
      );
      // If Wyzia returned a hosted bank-connect URL, redirect there.
      if (r.data?.page_url) {
        toast.success(`Redirecting to your bank to authorize ${money(topupAmount)}…`);
        window.location.href = r.data.page_url;
        return;
      }
      toast.success(`Added ${money(topupAmount)} to wallet via ${topupSource.toUpperCase()}`);
      setTopup(false); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend returns 403 + {code: "stepup_required"} → open the OTP modal
      if (e.response?.status === 403 && detail?.code === "stepup_required") {
        setTopupStepUp(true);
        return;
      }
      errorToastFromAxios(e, "Top-up failed", {
        subject: `Wallet top-up failed · ${money(topupAmount)} via ${topupSource.toUpperCase()}`,
        body: `I tried to add ${money(topupAmount)} to my verqo wallet via ${topupSource.toUpperCase()} and got an error.`,
        urgency: "high",
        category: "billing",
      });
    }
  };
  const addCard = async () => {
    if (newCard.type === "card") {
      if (!newCard.last4) return toast.error("Enter last 4 digits");
      if (!newCard.cardholder_name) return toast.error("Name on card is required");
      if (!newCard.billing_address_line1) return toast.error("Billing street is required");
      if (!newCard.billing_city) return toast.error("Billing city is required");
      if (!newCard.billing_postal_code) return toast.error("Billing ZIP is required");
      if (!newCard.cardholder_authorized) return toast.error("Please confirm cardholder authorization to continue");
    }
    if (newCard.type !== "card") {
      if (!newCard.bank_name) return toast.error("Bank name is required");
      if (!newCard.account_holder) return toast.error("Account holder (legal name) is required");
    }
    if (newCard.type === "ach" && (!/^\d{9}$/.test(newCard.routing) || newCard.account.length < 4)) {
      return toast.error("ACH needs a 9-digit routing # and account #");
    }
    if (newCard.type === "wire" && (!/^\d{9}$/.test(newCard.wire_routing) || newCard.account.length < 4)) {
      return toast.error("Domestic wire needs a 9-digit FedWire routing # and account #");
    }
    if (newCard.type === "intl_wire" && (!/^[A-Z0-9]{8}([A-Z0-9]{3})?$/i.test(newCard.swift_bic) || (newCard.iban.length < 8 && newCard.account.length < 4))) {
      return toast.error("International wire needs a SWIFT/BIC and an IBAN or account #");
    }
    const last4 = newCard.last4
      || (newCard.account ? newCard.account.slice(-4) : null)
      || (newCard.iban ? newCard.iban.replace(/\s/g, "").slice(-4) : "0000");
    const payload = {
      type: newCard.type === "card" ? "card" : "bank",
      label: newCard.label || (newCard.type === "card"
        ? `${newCard.brand.toUpperCase()} •••• ${last4}`
        : `${newCard.bank_name} ···${last4}`),
      brand: newCard.brand, last4, exp: newCard.exp,
    };
    if (newCard.type === "card") {
      payload.cardholder_name        = newCard.cardholder_name;
      payload.billing_address_line1  = newCard.billing_address_line1;
      payload.billing_address_line2  = newCard.billing_address_line2 || null;
      payload.billing_city           = newCard.billing_city;
      payload.billing_state          = newCard.billing_state || null;
      payload.billing_postal_code    = newCard.billing_postal_code;
      payload.billing_country        = newCard.billing_country || "US";
      payload.cardholder_authorized  = true;
    }
    if (newCard.type !== "card") {
      payload.bank_name      = newCard.bank_name;
      payload.account_holder = newCard.account_holder;
      payload.account_type   = newCard.account_type;
      payload.currency       = newCard.currency || "USD";
      payload.bank_country   = newCard.bank_country || "US";
      if (newCard.account) payload.account_number = newCard.account;
      if (newCard.type === "ach")        payload.routing_number      = newCard.routing;
      if (newCard.type === "wire")       payload.wire_routing_number = newCard.wire_routing;
      if (newCard.type === "intl_wire") {
        payload.swift_bic = newCard.swift_bic.toUpperCase().replace(/\s/g, "");
        if (newCard.iban) payload.iban = newCard.iban.toUpperCase().replace(/\s/g, "");
      }
    }
    const { data } = await api.post("/payment-methods", payload);
    if (data.mt_counterparty_id) {
      toast.success(`✓ Bank registered with Modern Treasury`);
    } else if (data.mt_error) {
      toast.warning(`Method saved, but Modern Treasury sync failed: ${data.mt_error}`);
    } else {
      toast.success(`${newCard.type.toUpperCase().replace("_", " ")} method added`);
    }
    setMethodModal(false);
    setNewCard({
      type: "card", brand: "visa", last4: "", exp: "", label: "",
      cardholder_name: "",
      billing_address_line1: "", billing_address_line2: "",
      billing_city: "", billing_state: "",
      billing_postal_code: "", billing_country: "US",
      cardholder_authorized: false,
      bank_name: "", account_holder: "", account_type: "checking",
      routing: "", account: "",
      wire_routing: "",
      swift_bic: "", iban: "", currency: "USD", bank_country: "US",
    });
    load();
  };
  const setDefault = async (id) => {
    await api.patch(`/payment-methods/${id}`, { is_default: true });
    toast.success("Default updated");
    load();
  };

  // ── the lender helpers ─────────────────────────────────────────────────────
  // Open the lender embedded checkout for the currently-open invoice with
  // the buyer's selected Net-X term. If their lender profile is incomplete,
  // pop the one-time confirm-details mini-form first.
  const startTwoCheckout = async (invoiceId, termsCode) => {
    if (!twoProfile?.complete) {
      // Pre-fill from KYB-derived profile if any
      const p = twoProfile?.profile;
      if (p) {
        setTwoProfileForm({
          company_name: p.company_name || "",
          organization_number: p.organization_number || "",
          first_name: p.first_name || "",
          last_name: p.last_name || "",
          phone: p.phone || "",
          country: p.country || "US",
          billing_address: p.billing_address || { street_address: "", city: "", postal_code: "", region: "", country: p.country || "US" },
        });
      }
      setTwoProfileOpen(true);
      return;
    }
    setTwoStarting(true);
    try {
      const { data } = await api.post(`/two/invoice/${invoiceId}/start`, { terms: termsCode });
      setTwoCheckout({
        paymentUrl: data.payment_url,
        amount: data.gross_amount ? `${data.currency || "USD"} ${data.gross_amount}` : "",
        invoiceId,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lender checkout failed to start");
    }
    setTwoStarting(false);
  };

  const saveTwoProfile = async () => {
    try {
      await api.post("/two/profile", twoProfileForm);
      const { data } = await api.get("/two/profile");
      setTwoProfile(data);
      setTwoProfileOpen(false);
      toast.success("Business details saved · ready to authorize via the lender");
      // If the user was mid-payment when prompted, resume.
      if (payInv) {
        setTimeout(() => startTwoCheckout(payInv, selectedTwoTerm), 200);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save profile");
    }
  };

  const requestTwoCreditCheck = async (amount = 50000) => {
    try {
      const { data } = await api.post("/two/credit-check", { requested_amount: amount });
      if (data.approved) {
        toast.success(`Lender approved your ${money(amount)} credit request`);
      } else {
        toast.error(`Lender declined: ${data.decline_reason || "not approved"}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Credit check failed");
    }
  };

  const onTwoSuccess = async () => {
    const invoiceId = twoCheckout?.invoiceId;
    setTwoCheckout(null);
    setPayInv(null);
    toast.success("Authorized via the lender · seller will start packing");
    if (invoiceId) {
      // Optimistic: tell our backend the invoice is authorized. Webhook will
      // confirm asynchronously.
      try {
        await api.patch(`/invoices/${invoiceId}/authorize`, { method: "two_inc" });
      } catch {
        /* webhook will catch up */
      }
    }
    nav("/buyer/orders#journey", { replace: true });
    load();
  };

  const onTwoCancel = () => {
    setTwoCheckout(null);
    toast.info("Lender authorization cancelled");
  };

  // ── Payment SMS step-up helpers (Feb 2026) ───────────────────────────
  // Step 1: fire /payments/sms-challenge — backend texts the OWNER, we
  //         show the OTP entry screen. Operator gets the code from the
  //         owner (text / call / in-person) and enters it.
  // Step 2: /payments/sms-verify — backend returns short-lived
  //         approval_token. We then call doPay() which passes the token
  //         through to /pay or /authorize.
  const requestStepupChallenge = async () => {
    if (!payInv) return;
    setStepupErr(null);
    setStepupCode("");
    setStepupState("challenging");
    try {
      const action = (payMode === "authorize" || payMethod === "seller_terms")
        ? "authorize_invoice" : "pay_invoice";
      const { data } = await api.post("/payments/sms-challenge", {
        invoice_id: payInv, action,
        amount: payInvoice?.amount,
      });
      // Demo-portal SMS bypass: backend already minted a demo-bypass
      // challenge. Skip the OTP entry UI entirely — go straight to the
      // verify call (which returns a stub approval token) and then doPay().
      if (data?.demo_bypass) {
        try {
          const { data: vd } = await api.post("/payments/sms-verify", {
            challenge_id: data.challenge_id,
            code: "000000", // ignored by the demo-bypass branch on the BE
          });
          setApprovalToken(vd.approval_token);
          setStepupState(null);
          setStepupCtx(null);
          setTimeout(() => doPay(), 50);
        } catch (err) {
          setStepupErr("Demo bypass failed unexpectedly.");
          setStepupState(null);
        }
        return;
      }
      setStepupCtx({
        challenge_id: data.challenge_id,
        phone_masked: data.phone_masked,
        owner_name:   data.owner_name,
        sent_to_owner: data.sent_to_owner,
        dev_mode:     data.dev_mode,
      });
      setStepupState("awaiting_code");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
               || e?.response?.data?.detail
               || "Couldn't send approval code to owner. Try again.";
      setStepupErr(typeof msg === "string" ? msg : "Approval send failed.");
      setStepupState(null);
    }
  };

  const verifyStepupCode = async () => {
    if (!stepupCtx?.challenge_id || stepupCode.length < 4) return;
    setStepupErr(null);
    setStepupState("verifying");
    try {
      const { data } = await api.post("/payments/sms-verify", {
        challenge_id: stepupCtx.challenge_id,
        code: stepupCode.trim(),
      });
      // Approval token is short-lived — set it AND immediately call
      // doPay(). The token gets consumed on the server when /pay runs.
      setApprovalToken(data.approval_token);
      setStepupState(null);
      setStepupCtx(null);
      setStepupCode("");
      // doPay reads `approvalToken` via React state — schedule it on the
      // next tick so the setState above has propagated.
      setTimeout(() => doPay(), 50);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
               || e?.response?.data?.detail
               || "That code didn't work. Try again.";
      setStepupErr(typeof msg === "string" ? msg : "Verification failed.");
      setStepupState("awaiting_code");
    }
  };

  const doPay = async () => {
    if (!payInv) return;
    // Phase 2 — flip to inline processing state so the button shows a
    // spinner instead of bouncing. The panel stays open so the user
    // sees in-flight progress + can see what's happening with their
    // wallet / card / ACH in real time.
    setPayState({ kind: "processing", message: null, details: null });
    try {
      // Feb 2026 — payment SMS step-up: doPay always carries the
      // approval_token issued by /payments/sms-verify. The endpoints
      // burn the token; replay attempts return 401.
      const _approval = approvalToken;
      if (payMethod === "seller_terms") {
        await api.patch(`/invoices/${payInv}/authorize`, {
          method: "seller_terms",
          seller_offer_id: sellerOfferId,
          extension: extension || undefined,
          approval_token: _approval,
        });
        const sellerOffer = sellerTerms.find(o => o.id === sellerOfferId);
        const msg = extension
          ? `Authorized via ${sellerOffer?.seller_name} free terms · extended to ${extension.toUpperCase()} via the lender`
          : `Authorized via ${sellerOffer?.seller_name} free ${sellerOffer?.terms?.toUpperCase()} — no fee`;
        setPayState({ kind: "success", message: "Payment authorized",
                       details: msg });
      } else if (payMode === "authorize") {
        await api.patch(`/invoices/${payInv}/authorize`, { method: payMethod, approval_token: _approval });
        setPayState({ kind: "success", message: "Authorized",
                       details: `We'll auto-debit ${payMethod.toUpperCase()} on the due date.` });
      } else {
        const payload = { method: payMethod, approval_token: _approval };
        const { data } = await api.post(`/invoices/${payInv}/pay`, payload);
        // Phase B (Feb 2026) — if payment exceeded co-approval threshold,
        // it's now held in escrow waiting for sign-off. Show a distinct
        // "pending approval" state instead of success.
        if (data.status === "pending_co_approval") {
          setPayState({ kind: "pending_approval",
            message: "Awaiting co-approval",
            details: data.message,
            request_id: data.request_id,
            approvers_pending: data.approvers_pending });
          setTimeout(() => {
            setPayInv(null);
            setApprovalToken(null);
            setPayState({ kind: "idle", message: null, details: null });
            load();
          }, 4500);
          return;
        }
        if (data.declined) {
          setPayState({ kind: "error",
            message: `Card declined · ${data.error || data.status} · Ref ${data.ref_num || ""}`,
            details: null });
          errorWithTicket(
            `Card declined · ${data.error || data.status} · Ref ${data.ref_num || ""}`,
            {
              subject: `Card declined paying invoice ${payInv}`,
              body:
                `My card was declined when I tried to pay invoice ${payInv}.\n` +
                `Method: ${payMethod}\n` +
                `Error from processor: ${data.error || data.status || "unknown"}\n` +
                `Ref: ${data.ref_num || "n/a"}\n\n` +
                `Please help — I need to settle this invoice.`,
              urgency: "high",
              category: "billing",
            }
          );
          return;
        }
        if (data.live_payment) {
          const msg = data.method === "ach"
              ? `ACH submitted · ${data.settles_in} · Ref ${data.ref_num}`
              : `${data.card_type || "Card"} ${data.masked_card?.slice(-4) ? "••" + data.masked_card.slice(-4) : ""} charged · Ref ${data.ref_num}`;
          setPayState({ kind: "success", message: "Payment complete",
                         details: msg });
        } else {
          setPayState({ kind: "success", message: "Payment authorized",
                         details: `Method: ${payMethod.toUpperCase()} · Seller will start packing.` });
        }
      }
      // Phase C — DON'T auto-close here. The PaymentSuccessWithUndo
      // component renders a 30s undo countdown and dispatches onTimerExpire
      // to close + redirect when the window ends. Auto-close-on-success
      // would deny the buyer their undo opportunity.
    } catch (e) {
      // Iter 156 · Backend now returns 400 {reason:"insufficient_funds"}
      // for wallet payments when wallet < invoice amount (the frontend's
      // disabled-button guard is no longer the only safety). Convert to
      // a friendly toast that opens the topup dialog pre-filled with the
      // exact shortfall + invoice context (auto-resume on webhook SUCCESS).
      const isf = e?.response?.status === 400
                  && e?.response?.data?.detail?.reason === "insufficient_funds";
      if (isf) {
        const d = e.response.data.detail;
        toast.error(`Wallet short by ${money(d.shortfall)} — top up and we'll auto-pay.`,
                    { duration: 7000 });
        // Pre-fill topup with the shortfall + tag the invoice.
        setTopupAmount(Math.ceil((d.shortfall || 0) * 100) / 100);
        setTopupPendingInvoice({
          id:         payInv,
          code:       payInvoice?.code,
          amount:     payInvoice?.amount,
          shortfall:  d.shortfall,
          sellerName: payInvoice?.seller_name,
        });
        setTopupSource("rfp");
        setPayInv(null);
        setPayState({ kind: "idle", message: null, details: null });
        setTopup(true);
        return;
      }
      // Phase IV — friendly handling for the 410 Gone returned when the
      // buyer tries to accept an expired invoice. Closes the payment
      // dialog, refreshes the list (so the now-voided invoice disappears
      // from the open list and shows in the void/closed view), and
      // surfaces a clear next step rather than a generic error.
      if (e?.response?.status === 410 && e.response?.data?.detail?.code === "invoice_expired") {
        const msg = e.response.data.detail?.message || "This invoice expired before you could pay.";
        toast.error(msg, { duration: 8000 });
        setPayInv(null);
        setSellerOfferId(null);
        setExtension(null);
        setPayState({ kind: "idle", message: null, details: null });
        load();
        return;
      }
      // Show inline error, let user retry without closing the panel.
      const friendlyMsg = e?.response?.data?.detail?.message
                       || e?.response?.data?.detail
                       || e?.message
                       || "Something went wrong with the payment.";
      setPayState({ kind: "error",
        message: typeof friendlyMsg === "string" ? friendlyMsg : "Payment failed",
        details: null });
      errorToastFromAxios(e, "Action failed", {
        subject: `Couldn't pay invoice ${payInv}`,
        body:
          `I tried to pay invoice ${payInv} via ${payMethod} and got an error.\n` +
          `Mode: ${payMode || "instant"}.`,
        urgency: "high",
        category: "billing",
      });
    }
  };

  return (
    <div className="min-h-screen" data-testid="buyer-payments">
      {/* Top section header — context only (sub-nav now lives in main sidebar) */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20 px-12 py-6 flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Banking · Payments</div>
          <h1 className="display text-2xl mt-1 tracking-tight capitalize">
            {section}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-2 rounded-full bg-foreground text-background px-4 py-1.5">
            <Wallet className="w-3.5 h-3.5 text-accent"/>
            <span className="text-[10px] mono uppercase tracking-[0.18em] text-accent">Wallet</span>
            <span className="display text-base">{money(wallet.balance)}</span>
          </div>
          <Button onClick={() => setTopup(true)} data-testid="header-topup"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-9 px-4">
            <Plus className="w-4 h-4 mr-1.5"/>Add funds
          </Button>
        </div>
      </div>

      {/* Sticky tab strip — shared with BuyerInvoicesList so it persists
          across the Payments hub + Invoices route. */}
      <PaymentsTabBar role="buyer"/>

      {/* Main content */}
      <div className="page-shell pb-20">

          {/* ───── OVERVIEW ───── */}
          {section === "overview" && (
            <>
              <div className="text-xs mono uppercase tracking-[0.2em] text-foreground/50">Money flow · last 30 days</div>
              <h2 className="display text-5xl tracking-[-0.02em] leading-[1.05] max-w-2xl mt-3">
                Your business, by the dollar.
              </h2>
              <p className="text-sm text-foreground/55 mt-3 max-w-xl">
                One place for every dollar that moves in and out — wallet, credit, methods, terms, lenders.
              </p>

              {/* 4-up flow card */}
              <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
                <FlowTile icon={ArrowDownLeft} label="Money In" value={money(flow.inflow)} hint="last 30d" tone="positive" testid="flow-in"/>
                <FlowTile icon={ArrowUpRight} label="Money Out" value={money(flow.outflow)} hint="last 30d" testid="flow-out"/>
                <FlowTile icon={Wallet} label="Wallet" value={money(wallet.balance)} hint="available now" testid="flow-wallet"/>
                <FlowTile icon={Landmark} label="Credit" value={money(cap.available)} hint={`of ${money(cap.credit_limit)} line`} testid="flow-credit"/>
              </div>

              {/* Quick actions */}
              <div className="mt-12">
                <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50 mb-4">Quick actions</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                  <QuickAction icon={Plus} label="Add to wallet" onClick={() => setTopup(true)} testid="qa-topup"/>
                  <QuickAction icon={CreditCard} label="Pay invoices" href="/buyer/orders" testid="qa-pay"/>
                  <QuickAction icon={Landmark} label="Apply for credit" onClick={() => setApplyOpen({ id: "supply_chain", name: "Net-Terms", isTwo: true })} testid="qa-credit"/>
                  <QuickAction icon={Banknote} label="Add card / bank" onClick={() => setMethodModal(true)} testid="qa-method"/>
                </div>
              </div>

              {/* Recent activity preview */}
              <div className="mt-14">
                <div className="flex items-end justify-between mb-5">
                  <div>
                    <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Recent activity</div>
                    <h3 className="display text-2xl mt-1.5">Last 5 movements</h3>
                  </div>
                  <button onClick={() => goSection("activity")} data-testid="see-all-activity"
                    className="text-xs text-foreground/60 hover:text-foreground inline-flex items-center">
                    See all <ArrowRight className="w-3 h-3 ml-1"/>
                  </button>
                </div>
                <ActivityList items={buildActivity(scopedInvoices, wallet).slice(0, 5)} />
              </div>
            </>
          )}

          {/* ───── WALLET ───── */}
          {section === "wallet" && (
            <>
              <div className="text-xs mono uppercase tracking-[0.2em] text-foreground/50">Wallet</div>
              <div className="display text-7xl mt-3 tracking-[-0.03em]" data-testid="wallet-balance">{money(wallet.balance)}</div>
              <div className="text-sm text-foreground/55 mt-2">USD · settles instantly · used to pay invoices, fund POs, reload cards</div>

              <div className="mt-10 flex gap-2 flex-wrap">
                <Button onClick={() => setTopup(true)} data-testid="wallet-topup"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5">
                  <Plus className="w-4 h-4 mr-1.5"/>Add funds
                </Button>
                <Button variant="ghost" onClick={() => goSection("methods")} className="rounded-full h-11 px-5 text-foreground/60">
                  Manage funding sources
                </Button>
              </div>
              <p className="mt-2 text-[11px] text-foreground/45">Wallet funds can be used to pay invoices and shipping. Withdrawals to your own bank are not supported on buyer accounts.</p>

              {/* The buyer's own account number — fund the wallet by ACH or wire */}
              <div className="mt-8 grid gap-4 lg:grid-cols-2">
                <VerqoAccountCard role="buyer" onChange={load} />
                <LimitRequestCard />
              </div>

              {/* Why wallet — clean educational rail */}
              <div className="mt-14 grid lg:grid-cols-2 gap-12">
                <div>
                  <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50 mb-4">Recent transactions</div>
                  <ActivityList items={(wallet.transactions || []).slice(0, 8).map(tx => {
                    const isCredit = tx.type === "credit_memo_received";
                    const isRefund = tx.type === "refund_received";
                    const isTopup  = tx.type === "topup";
                    const title = isCredit
                      ? `Credit memo · ${tx.invoice_code || ""}`.trim()
                      : isRefund ? "Refund received"
                      : isTopup  ? "Wallet top-up"
                      : "Invoice payment";
                    const sub = isCredit
                      ? `${tx.credit_memo_code || tx.ref} · against ${tx.invoice_code || "invoice"}`
                      : `${tx.method?.toUpperCase()} · ${tx.ref}`;
                    return { title, sub, amount: tx.amount, date: tx.created_at };
                  })} />
                </div>
                <aside>
                  <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50 mb-4">Why a wallet?</div>
                  <ul className="space-y-3.5">
                    {[
                      { i: Zap, t: "Instant supplier payouts", d: "Sellers ship faster when paid from wallet" },
                      { i: Shield, t: "FDIC-insured up to $250k", d: "Funds held in partner bank accounts" },
                      { i: Lock, t: "Avoid 3–4% card fees", d: "Wallet → wallet pays no card processing fee" },
                    ].map((b, i) => (
                      <li key={i} className="flex gap-3">
                        <div className="w-9 h-9 shrink-0 rounded-lg bg-secondary grid place-items-center"><b.i className="w-4 h-4"/></div>
                        <div>
                          <div className="text-sm font-medium">{b.t}</div>
                          <div className="text-xs text-foreground/55 mt-0.5">{b.d}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </aside>
              </div>
            </>
          )}

          {/* ───── METHODS ───── */}
          {section === "methods" && (
            <>
              <div className="text-xs mono uppercase tracking-[0.2em] text-foreground/50">Payment methods</div>
              <h2 className="display text-4xl tracking-[-0.02em] leading-tight mt-3 max-w-xl">Cards, banks &amp; wallets — all in one place.</h2>

              {/* Plaid bank connection — instant ACH + cashflow underwriting */}
              <div className="mt-8" data-testid="plaid-section">
                {plaidSummary?.connected ? (
                  <PlaidConnectionCard
                    summary={plaidSummary}
                    refreshing={plaidRefreshing}
                    onRefresh={async () => {
                      setPlaidRefreshing(true);
                      try {
                        await api.post("/onboarding/plaid/refresh", {});
                        const r = await api.get("/onboarding/plaid/summary");
                        setPlaidSummary(r.data);
                        toast.success("Cashflow refreshed");
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Refresh failed");
                      } finally {
                        setPlaidRefreshing(false);
                      }
                    }}
                  />
                ) : (
                  <div className="rounded-2xl border border-border bg-secondary/30 p-6 grid md:grid-cols-[1.4fr_auto] items-center gap-6" data-testid="plaid-cta-card">
                    <div>
                      <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Bank-verified ACH · powered by Plaid</div>
                      <div className="display text-xl mt-2">Connect your bank in 30 seconds.</div>
                      <div className="text-sm text-foreground/65 mt-1">
                        Verified routing &amp; account numbers · 24-month cashflow snapshot for instant credit decisions · no manual micro-deposits.
                      </div>
                    </div>
                    <PlaidLinkButton
                      onConnected={async () => {
                        const r = await api.get("/onboarding/plaid/summary");
                        setPlaidSummary(r.data);
                        load();
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="mt-10 flex justify-between items-end">
                <div className="text-sm text-foreground/55">{methods.length} method{methods.length !== 1 ? "s" : ""} on file</div>
                <Button onClick={() => setMethodModal(true)} data-testid="method-add"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4">
                  <Plus className="w-4 h-4 mr-1.5"/>Add method
                </Button>
              </div>

              <div className="mt-6 space-y-2">
                {methods.map(m => (
                  <BuyerMethodRow key={m.id} m={m} onSetDefault={() => setDefault(m.id)}/>
                ))}
                {methods.length === 0 && <div className="py-16 text-center text-foreground/40 text-sm">No methods yet</div>}
              </div>
            </>
          )}

          {/* ───── CREDIT & TERMS ───── */}
          {section === "credit" && (
            <>
              {cap.credit_limit === 0 ? (
                /* No credit yet — Apply state */
                <div className="rounded-2xl border border-border bg-white p-10 grid md:grid-cols-[1.2fr_1fr] gap-10 items-center" data-testid="credit-apply-state">
                  <div>
                    <div className="text-xs mono uppercase tracking-[0.2em] text-foreground/50">Credit line</div>
                    <h2 className="display text-5xl mt-3 tracking-[-0.02em] leading-[1.05]">No credit yet — let's fix that.</h2>
                    <p className="text-sm text-foreground/55 mt-3 max-w-md">
                      Apply once and the lender underwrites your line in real time using your verqo Trade Score, KYB & Plaid signals. Limits unlock as you order.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-2">
                      <Button onClick={() => setApplyOpen({ id: "supply_chain", name: "Net-Terms", isTwo: true })}
                        className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5" data-testid="credit-apply">
                        Apply for credit <ArrowRight className="w-4 h-4 ml-1.5"/>
                      </Button>
                      <Button variant="outline" onClick={() => nav("/buyer/score")} className="rounded-full h-11 px-5">
                        Check my Score
                      </Button>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[11px] text-foreground/55">
                      <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3"/>No hard credit pull</span>
                      <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3"/>Decision in 30 seconds</span>
                      <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3"/>Up to $250k starting line</span>
                    </div>
                  </div>
                  <div className="rounded-2xl bg-secondary/40 p-7">
                    <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50 mb-4">How credit works on verqo</div>
                    <ol className="space-y-4 text-sm">
                      {[
                        { n: 1, t: "Onboard & build a Score", d: "Verify business, link bank, complete first order" },
                        { n: 2, t: "Get pre-approved", d: "Score above 600 = instant approval up to $50k" },
                        { n: 3, t: "Choose your terms at checkout", d: "Net-14 / 30 / 60 / 90 — auto-deducted on the due date" },
                        { n: 4, t: "Limits grow as you pay on time", d: "Pay early to unlock larger lines and lower rates" },
                      ].map(s => (
                        <li key={s.n} className="flex gap-3">
                          <div className="w-6 h-6 rounded-full bg-foreground text-background grid place-items-center text-[11px] font-medium shrink-0">{s.n}</div>
                          <div>
                            <div className="font-medium">{s.t}</div>
                            <div className="text-xs text-foreground/55 mt-0.5">{s.d}</div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              ) : (
                /* Has credit — show line + Request increase */
                <div className="grid md:grid-cols-2 gap-12 items-end">
                  <div>
                    <div className="text-xs mono uppercase tracking-[0.2em] text-foreground/50">Credit line</div>
                    <div className="display text-7xl mt-3 tracking-[-0.03em]" data-testid="credit-available">{money(cap.available)}</div>
                    <div className="text-sm text-foreground/55 mt-2">available · {money(cap.outstanding)} drawn of {money(cap.credit_limit)} limit</div>
                    <div className="mt-6 h-1 bg-secondary rounded-full overflow-hidden max-w-md">
                      <div className="h-full bg-foreground" style={{ width: `${cap.utilization_pct}%` }}/>
                    </div>
                    <div className="mt-2 text-xs text-foreground/50 max-w-md">
                      {cap.utilization_pct}% utilization · {cap.utilization_pct < 30 ? "Excellent · ready for credit increase" : "Healthy"}
                    </div>
                    <div className="mt-8 flex gap-2">
                      <Button onClick={() => setApplyOpen({ id: "increase", name: "Credit line increase", isTwo: true })}
                        className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-10 px-4" data-testid="credit-increase">
                        Request increase
                      </Button>
                      <Button variant="outline" onClick={() => toast.info("Statement download coming soon")} className="rounded-full h-10 px-4">
                        Download statement
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-px bg-border rounded-2xl overflow-hidden border border-border">
                    {[
                      { l: "Rate", v: `${(cap.rate * 100).toFixed(2)}%` },
                      { l: "Score band", v: cap.credit_limit > 30000 ? "Excellent" : "Good" },
                      { l: "Active terms", v: "Net-30" },
                      { l: "Member since", v: "Jan 2026" },
                    ].map((s, i) => (
                      <div key={i} className="bg-background p-5">
                        <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{s.l}</div>
                        <div className="display text-xl mt-2">{s.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Terms */}
              <div className="mt-16">
                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                  <div>
                    <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Payment terms at checkout</div>
                    <h3 className="display text-3xl mt-2">Choose how you want to pay.</h3>
                  </div>
                  <div className="text-xs text-foreground/55 max-w-xs">All terms underwritten in real-time by the lender · click to select your default. Switch any time.</div>
                </div>
                <div className="md:grid-cols-4 grid grid-cols-1 gap-3" data-testid="two-terms-grid">
                  {(twoTerms.length > 0 ? twoTerms : [
                    { code: "net14", label: "Net-14", days: 14, fee_pct: 0.0, description: "Pay within 14 days" },
                    { code: "net30", label: "Net-30", days: 30, fee_pct: 0.0, description: "Pay within 30 days", popular: true },
                    { code: "net60", label: "Net-60", days: 60, fee_pct: 0.5, description: "Pay within 60 days" },
                    { code: "net90", label: "Net-90", days: 90, fee_pct: 1.2, description: "Pay within 90 days" },
                  ]).map(t => {
                    const active = selectedTwoTerm === t.code;
                    return (
                      <button
                        key={t.code}
                        onClick={() => { setSelectedTwoTerm(t.code); toast.success(`Default term set to ${t.label}`); }}
                        data-testid={`term-${t.code}`}
                        className={`text-left rounded-2xl border p-5 transition-colors ${active ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/40 hover:bg-secondary/30"}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="display text-lg">{t.label}</div>
                          {active && <span className="chip bg-accent/30 border border-accent/40 text-[10px]">Active</span>}
                          {t.popular && !active && <span className="chip bg-secondary text-[10px]">Popular</span>}
                        </div>
                        <div className="text-xs text-foreground/60 mt-2">{t.description || `Pay within ${t.days} days`}</div>
                        <div className="text-[11px] mono text-foreground/50 mt-3">
                          {t.fee_pct > 0 ? `${t.fee_pct}% fee` : "0% fee"}
                        </div>
                        {!active && (
                          <div className="mt-4 text-xs text-foreground/70 hover:text-foreground inline-flex items-center" data-testid={`use-${t.code}`}>
                            Use this <ArrowRight className="w-3 h-3 ml-1"/>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 text-[11px] text-foreground/50 inline-flex items-center gap-1.5">
                  <Shield className="w-3 h-3"/>Net terms financed by the lender · seller paid same-day on your authorization
                </div>
              </div>

              {/* Prepayment & Auto-debit — pro-grade controls */}
              <div className="mt-16">
                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                  <div>
                    <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Auto-pay &amp; prepayment</div>
                    <h3 className="display text-3xl mt-2">Set it once, forget the due-dates.</h3>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-border bg-white p-5" data-testid="autopay-card">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="display text-lg">Auto-debit on due date</div>
                        <div className="text-xs text-foreground/55 mt-1 max-w-sm">
                          We pull funds via ACH from your default bank exactly on the invoice due date. No late fees, no manual clicks.
                        </div>
                      </div>
                      <button onClick={() => setAutoDebit(v => !v)} data-testid="autopay-toggle"
                        className={`relative w-11 h-6 rounded-full transition-colors ${autoDebit ? "bg-foreground" : "bg-secondary"}`}>
                        <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${autoDebit ? "translate-x-5" : "translate-x-0.5"}`}/>
                      </button>
                    </div>
                    <div className="mt-4 text-[11px] text-foreground/55 inline-flex items-center gap-2">
                      <Building2 className="w-3 h-3"/>{methods.find(m => m.is_default)?.label || methods[0]?.label || "No default method"} · {autoDebit ? "Active" : "Off"}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border bg-white p-5">
                    <div className="display text-lg">Prepay an invoice</div>
                    <div className="text-xs text-foreground/55 mt-1 max-w-sm">
                      Pay early to unlock a 1.5% discount per 30 days off due date. Boosts your verqo Trade Score and unlocks larger credit lines.
                    </div>
                    <Button onClick={() => {
                      const el = document.querySelector('[data-testid="open-balances-section"]');
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }} data-testid="prepay-cta" className="mt-4 rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90">
                      Choose invoice to prepay <ArrowRight className="w-3.5 h-3.5 ml-1.5"/>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Open balances — manual pay-down panel */}
              <div className="mt-10" data-testid="open-balances-section">
                {!autoDebit && (
                  <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="autopay-off-notice">
                    <AlertCircle/>
                    <div className="text-sm">
                      <div className="font-medium text-amber-900">Auto-debit is off — you'll pay these manually</div>
                      <div className="text-xs text-amber-800 mt-0.5">Late payments hurt your verqo Trade Score and credit limits. Settle balances before their due date below.</div>
                    </div>
                  </div>
                )}
                <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
                  <div>
                    <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Open balances</div>
                    <h3 className="display text-3xl mt-2">Pay invoices &amp; credit draws</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">Total outstanding</div>
                    <div className="display text-3xl mt-1 tracking-tight" data-testid="total-outstanding">
                      {money(scopedInvoices.filter(i => ["sent","outstanding","overdue"].includes(i.status)).reduce((s,i)=>s+i.amount,0) + cap.outstanding)}
                    </div>
                  </div>
                </div>

                <OpenBalancesList
                  invoices={scopedInvoices}
                  outstanding={cap.outstanding}
                  rate={cap.rate}
                  onPay={(invId) => nav(`/buyer/payments?pay=${invId}#credit`)}
                  onPayDraw={() => toast.success("Credit draw payment scheduled")}
                />
              </div>

              {/* Payment history */}
              <div className="mt-16">
                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                  <div>
                    <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Payment history</div>
                    <h3 className="display text-3xl mt-2">Last 12 months · paid in full.</h3>
                  </div>
                  <button onClick={() => toast.info("Statement download coming soon")} className="text-xs text-foreground/60 hover:text-foreground inline-flex items-center">
                    <Download className="w-3.5 h-3.5 mr-1"/>Export CSV
                  </button>
                </div>
                <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="payment-history">
                  <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 bg-secondary/40 border-b border-border">
                    <div className="col-span-3">Invoice</div>
                    <div className="col-span-3">Seller</div>
                    <div className="col-span-2">Method</div>
                    <div className="col-span-2">Paid on</div>
                    <div className="col-span-2 text-right">Amount</div>
                  </div>
                  <div className="divide-y divide-border max-h-80 overflow-y-auto">
                    {scopedInvoices.filter(i => i.status === "paid").slice(0, 8).map(i => (
                      <button key={i.id} type="button" onClick={() => setPaidViewInvoice(i)}
                              data-testid={`paid-invoice-${i.code}`}
                              className="w-full text-left grid grid-cols-12 gap-3 px-5 py-3 items-center text-sm hover:bg-secondary/30 transition-colors">
                        <div className="col-span-3">
                          <div className="font-medium">{i.code}</div>
                          <div className="text-[10px] mono text-foreground/50">{i.terms?.toUpperCase()}</div>
                        </div>
                        <div className="col-span-3 text-foreground/70">{i.seller_name}</div>
                        <div className="col-span-2 text-xs text-foreground/60 inline-flex items-center gap-1.5"><Building2 className="w-3 h-3"/>{i.paid_method?.toUpperCase() || "ACH"}</div>
                        <div className="col-span-2 text-xs text-foreground/60">{new Date(i.created_at).toLocaleDateString()}</div>
                        <div className="col-span-2 text-right">
                          <div className="display">{money(i.amount)}</div>
                          {i.shipping_passthrough && Number(i.shipping_passthrough.billed) > 0 && (
                            <div className="text-[10px] text-foreground/50 inline-flex items-center gap-0.5 mt-0.5">
                              <Truck className="w-2.5 h-2.5"/>
                              incl. {money(i.shipping_passthrough.billed)}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                    {scopedInvoices.filter(i => i.status === "paid").length === 0 && (
                      <div className="py-12 text-center text-foreground/40 text-sm">No payments yet</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Lenders */}
              <div className="mt-16">
                <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                  <div>
                    <div className="text-xs mono uppercase tracking-[0.15em] text-foreground/50">Credit marketplace</div>
                    <h3 className="display text-3xl mt-2">Or borrow from <span className="italic text-foreground/60">other lenders.</span></h3>
                  </div>
                  <div className="text-xs text-foreground/55 max-w-xs">Your verqo Trade Score will be shared with each lender for a unified rate quote — no hard pulls.</div>
                </div>
                <div className="space-y-2" data-testid="lender-marketplace">
                  {lenders.map((l, idx) => (
                    <div key={l.id} className="grid grid-cols-12 gap-4 items-center py-5 border-y border-border -mx-4 px-4 rounded-md transition-colors hover:bg-secondary/20" data-testid={`lender-${idx}`}>
                      <div className="col-span-1">
                        <div className="w-10 h-10 rounded-xl bg-secondary text-foreground/40 grid place-items-center display text-lg">·</div>
                      </div>
                      <div className="col-span-4">
                        <div className="font-medium flex items-center gap-2 text-foreground/65">
                          Lender #{idx + 1}
                          <span className="chip bg-secondary border border-border text-[9px] uppercase tracking-wider text-foreground/55">Coming soon</span>
                        </div>
                        <div className="text-xs text-foreground/45 mt-0.5">Capital partner integration in progress</div>
                      </div>
                      <div className="col-span-2 text-sm text-foreground/40">
                        <div>—</div>
                        <div className="text-[10px] uppercase tracking-wider">APR range</div>
                      </div>
                      <div className="col-span-2 text-sm text-foreground/40">
                        <div>—</div>
                        <div className="text-[10px] uppercase tracking-wider">limit</div>
                      </div>
                      <div className="col-span-1 text-xs text-foreground/40">—</div>
                      <div className="col-span-2 flex justify-end">
                        <Button size="sm" disabled className="rounded-full h-9 px-4 bg-secondary text-foreground/45 cursor-not-allowed">
                          Coming soon
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ───── PRE-CREDIT ───── */}
          {section === "pre-credit" && (
            <PreCreditPanel
              walletBalance={Number(wallet?.balance) || 0}
              onChanged={() => {
                // refresh wallet (a deposit drops the buyer's wallet balance)
                api.get("/wallet").then(r => setWallet(r.data)).catch(() => {});
              }}
            />
          )}

          {/* ───── ACTIVITY ───── */}
          {section === "activity" && (
            <WalletActivityPanel
              title="Every dollar in & out."
              subtitle="Wallet, credit, invoices, payouts — all unified. Smart filter, then download a signed monthly statement."
              testIdPrefix="activity"
              role="buyer"
              extraItems={invoiceActivityItems}
            />
          )}

        </div>

      {/* ───── Add funds dialog · mirrors seller wallet UX (Feb 2026) ───── */}
      <Dialog
        open={topup}
        onOpenChange={(v) => {
          setTopup(v);
          // Iter 156 · clear the pending-invoice context when the dialog
          // closes (so a normal Add-funds click later doesn't accidentally
          // re-tag with a stale invoice id).
          if (!v) setTopupPendingInvoice(null);
        }}
      >
        <DialogContent className="max-w-md" data-testid="buyer-topup-dialog">
          <DialogHeader>
            <DialogTitle>Add funds to your wallet</DialogTitle>
          </DialogHeader>
          {topupPendingInvoice ? (
            <div
              className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3.5 mb-3"
              data-testid="topup-autopay-banner"
            >
              <div className="text-[12.5px] font-medium text-emerald-900 mb-0.5">
                We&apos;ll auto-pay invoice {topupPendingInvoice.code || ""} once your top-up clears
              </div>
              <div className="text-[11px] text-emerald-800/85 leading-snug">
                Pre-filled with the {money(topupPendingInvoice.shortfall)} shortfall on your{" "}
                {money(topupPendingInvoice.amount)} bill from {topupPendingInvoice.sellerName || "the seller"}. RFP is fastest (minutes).
              </div>
            </div>
          ) : (
            <p className="text-[12.5px] text-foreground/65 leading-snug mb-4">
              Pre-load your verqo wallet to pay invoices instantly without
              re-entering bank or card details. Pick the funding rail that
              matches how fast you need the money to post.
            </p>
          )}
          <div className="space-y-4">
            <div>
              <Label className="text-[12px]">Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/45 text-sm">$</span>
                <Input
                  type="number"
                  step="50"
                  min="50"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  className="pl-7 mono text-base h-11"
                  data-testid="buyer-topup-amount"
                  autoFocus
                />
              </div>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {[100, 500, 1000, 2500, 5000].map(a => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setTopupAmount(a)}
                    data-testid={`buyer-topup-quick-${a}`}
                    className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${Number(topupAmount) === a ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:bg-secondary/40"}`}
                  >
                    ${a.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[12px]">Funding method</Label>
              <div className="grid gap-2 mt-1.5">
                {[
                  { v: "ach",         icon: Building2,  l: "ACH · standard (next-day)", fee: "$0.50",  hint: "Cheapest · posts in 1 business day" },
                  { v: "ach_sameday", icon: Activity,   l: "ACH · same-day",            fee: "$3.50",  hint: "Posts within hours" },
                  { v: "rfp",         icon: TrendingUp, l: "Instant Payment Request",   fee: "$5.00",  hint: "Funds within 30 min" },
                  { v: "wire",        icon: Building2,  l: "Wire · domestic",           fee: "$15.00", hint: "Same-day for very large transfers" },
                  { v: "card",        icon: CreditCard, l: "Credit / debit card",       fee: "4% (3% Growth+)", hint: "Instant · highest cost" },
                ].map(opt => {
                  const Icon = opt.icon;
                  const active = topupSource === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setTopupSource(opt.v)}
                      data-testid={`buyer-topup-method-${opt.v}`}
                      className={`flex items-center gap-3 rounded-lg px-3.5 py-2.5 border text-left transition-colors ${active ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:bg-secondary/40"}`}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${active ? "" : "text-foreground/55"}`}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium">{opt.l}</div>
                        <div className={`text-[10.5px] mt-0.5 ${active ? "text-background/65" : "text-foreground/50"}`}>{opt.hint}</div>
                      </div>
                      <div className={`text-[11px] mono font-semibold ${active ? "text-background" : "text-foreground/75"}`}>{opt.fee}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <Button variant="ghost" onClick={() => setTopup(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={() => doTopup()} data-testid="confirm-topup"
              className="rounded-full bg-foreground text-background hover:bg-foreground/90">
              <Plus className="w-4 h-4 mr-1.5"/>
              Add {topupAmount ? money(Number(topupAmount)) : ""}
            </Button>
          </div>
          <div className="mt-2 text-center text-[10px] text-foreground/45 inline-flex items-center justify-center gap-2 w-full">
            <Lock className="w-2.5 h-2.5"/>SSL · Bank-grade security
          </div>
        </DialogContent>
      </Dialog>

      {/* ───── SMS step-up confirmation for wallet top-up ───── */}
      <StepUpModal
        open={topupStepUp}
        onClose={() => setTopupStepUp(false)}
        action="wallet_topup"
        amount={Number(topupAmount)}
        title={`Confirm ${money(topupAmount)} top-up`}
        description="For your security, we send a one-time code to your phone for every wallet funding."
        onVerified={(token) => doTopup(token)}
      />

      {/* ───── Add method dialog (Card / ACH / Wire) ───── */}
      <Dialog open={methodModal} onOpenChange={setMethodModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="display text-2xl tracking-tight">Add payment method</DialogTitle></DialogHeader>
          <div className="text-[10px] text-foreground/50 -mt-1">MOCK form</div>

          {/* Type tabs */}
          <div className="mt-3 grid grid-cols-4 gap-1.5">
            {[
              { k: "card", l: "Card", icon: CreditCard },
              { k: "ach", l: "ACH", icon: Building2 },
              { k: "wire", l: "Wire", icon: Banknote },
              { k: "intl_wire", l: "Intl", icon: Banknote },
            ].map(t => (
              <button key={t.k} onClick={() => setNewCard({ ...newCard, type: t.k })} data-testid={`method-type-${t.k}`}
                className={`rounded-xl border py-3 text-xs flex flex-col items-center gap-1 transition-colors ${newCard.type === t.k ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}>
                <t.icon className="w-4 h-4"/>{t.l}
              </button>
            ))}
          </div>

          <div className="mt-4 space-y-3">
            {newCard.type === "card" && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <select value={newCard.brand} onChange={e => setNewCard({ ...newCard, brand: e.target.value })}
                    className="h-11 rounded-md border border-input bg-white px-3 text-sm">
                    <option value="visa">Visa</option>
                    <option value="mastercard">Mastercard</option>
                    <option value="amex">Amex</option>
                    <option value="discover">Discover</option>
                  </select>
                  <Input value={newCard.last4} onChange={e => setNewCard({ ...newCard, last4: e.target.value.slice(0, 4) })}
                    placeholder="Last 4" data-testid="card-last4" className="h-11"/>
                </div>
                <Input value={newCard.exp} onChange={e => setNewCard({ ...newCard, exp: e.target.value })} placeholder="Expiry MM/YY" className="h-11"/>
                <Input value={newCard.cardholder_name} onChange={e => setNewCard({ ...newCard, cardholder_name: e.target.value })}
                  placeholder="Name on card" data-testid="card-name" className="h-11"/>
                <Input value={newCard.billing_address_line1} onChange={e => setNewCard({ ...newCard, billing_address_line1: e.target.value })}
                  placeholder="Billing street address" data-testid="card-addr-1" className="h-11"/>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={newCard.billing_city} onChange={e => setNewCard({ ...newCard, billing_city: e.target.value })}
                    placeholder="City" data-testid="card-city" className="h-11"/>
                  <Input value={newCard.billing_state} onChange={e => setNewCard({ ...newCard, billing_state: e.target.value })}
                    placeholder="State" data-testid="card-state" className="h-11"/>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={newCard.billing_postal_code} onChange={e => setNewCard({ ...newCard, billing_postal_code: e.target.value })}
                    placeholder="ZIP / Postal code" data-testid="card-zip" className="h-11"/>
                  <Input value={newCard.billing_country} onChange={e => setNewCard({ ...newCard, billing_country: e.target.value.toUpperCase().slice(0, 2) })}
                    placeholder="Country (US)" data-testid="card-country" className="h-11 font-mono uppercase" maxLength={2}/>
                </div>
                <label
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    newCard.cardholder_authorized
                      ? "border-emerald-500/40 bg-emerald-50/40"
                      : "border-foreground/20 bg-secondary/30 hover:border-foreground/40"
                  }`}
                  data-testid="card-auth-toggle"
                >
                  <input
                    type="checkbox"
                    checked={newCard.cardholder_authorized}
                    onChange={e => setNewCard({ ...newCard, cardholder_authorized: e.target.checked })}
                    data-testid="card-auth-checkbox"
                    className="mt-0.5 w-4 h-4 accent-emerald-600 shrink-0"
                  />
                  <span className="text-[11.5px] text-foreground/75 leading-snug">
                    <strong>I authorize this card.</strong> I confirm I am the cardholder (or an authorized user), and I agree not to dispute valid transactions charged to this card. Logged with my IP, browser, and timestamp.
                  </span>
                </label>
              </>
            )}
            {newCard.type !== "card" && (
              <>
                <Input value={newCard.bank_name} onChange={e => setNewCard({ ...newCard, bank_name: e.target.value })}
                  placeholder="Bank name (e.g. Chase, HSBC UK)" data-testid="bank-name" className="h-11"/>
                <Input value={newCard.account_holder} onChange={e => setNewCard({ ...newCard, account_holder: e.target.value })}
                  placeholder="Account holder (legal entity name)" data-testid="bank-holder" className="h-11"/>
                <div className="grid grid-cols-2 gap-2">
                  <select value={newCard.account_type} onChange={e => setNewCard({ ...newCard, account_type: e.target.value })}
                    data-testid="bank-type" className="h-11 rounded-md border border-input bg-white px-3 text-sm">
                    <option value="checking">Checking</option>
                    <option value="savings">Savings</option>
                    <option value="business_checking">Business Checking</option>
                    <option value="business_savings">Business Savings</option>
                  </select>
                  <select value={newCard.currency} onChange={e => setNewCard({ ...newCard, currency: e.target.value })}
                    data-testid="bank-currency" className="h-11 rounded-md border border-input bg-white px-3 text-sm">
                    <option value="USD">USD</option><option value="EUR">EUR</option>
                    <option value="GBP">GBP</option><option value="CAD">CAD</option>
                    <option value="MXN">MXN</option><option value="JPY">JPY</option>
                    <option value="CNY">CNY</option><option value="AUD">AUD</option>
                    <option value="INR">INR</option>
                  </select>
                </div>
              </>
            )}
            {newCard.type === "ach" && (
              <>
                <Input value={newCard.routing} onChange={e => setNewCard({ ...newCard, routing: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                  placeholder="ACH routing number (9 digits)" data-testid="ach-routing" className="h-11 font-mono"/>
                <Input value={newCard.account} onChange={e => setNewCard({ ...newCard, account: e.target.value.replace(/\D/g, "").slice(0, 17) })}
                  placeholder="Account number" data-testid="ach-account" className="h-11 font-mono"/>
              </>
            )}
            {newCard.type === "wire" && (
              <>
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2" data-testid="wire-fraud-warning">
                  <span aria-hidden className="font-bold mt-px">⚠️</span>
                  <span><strong>Wire-fraud warning.</strong> We will <strong>never</strong> email or message you to change wire instructions. Verify any such request by phone with a trusted contact before saving.</span>
                </div>
                <Input value={newCard.wire_routing} onChange={e => setNewCard({ ...newCard, wire_routing: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                  placeholder="FedWire routing number (9 digits)" data-testid="wire-routing" className="h-11 font-mono"/>
                <Input value={newCard.account} onChange={e => setNewCard({ ...newCard, account: e.target.value.replace(/\D/g, "").slice(0, 17) })}
                  placeholder="Account number" data-testid="wire-account" className="h-11 font-mono"/>
                <div className="text-[10.5px] text-foreground/55 -mt-1">Tip: FedWire routing # is often different from ACH routing # — check the “For wire” line on your bank's statement.</div>
              </>
            )}
            {newCard.type === "intl_wire" && (
              <>
                <Input value={newCard.swift_bic} onChange={e => setNewCard({ ...newCard, swift_bic: e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 11) })}
                  placeholder="SWIFT / BIC (8 or 11 chars)" data-testid="wire-swift" className="h-11 font-mono uppercase"/>
                <Input value={newCard.iban} onChange={e => setNewCard({ ...newCard, iban: e.target.value.toUpperCase().slice(0, 42) })}
                  placeholder="IBAN (optional, ≤34 chars)" data-testid="wire-iban" className="h-11 font-mono uppercase"/>
                <Input value={newCard.account} onChange={e => setNewCard({ ...newCard, account: e.target.value.replace(/\s/g, "").slice(0, 34) })}
                  placeholder="Account number (if no IBAN)" data-testid="wire-account" className="h-11 font-mono"/>
                <Input value={newCard.bank_country} onChange={e => setNewCard({ ...newCard, bank_country: e.target.value.toUpperCase().slice(0, 2) })}
                  placeholder="Bank country (ISO 2-letter, e.g. GB)" data-testid="wire-country" className="h-11 font-mono uppercase" maxLength={2}/>
              </>
            )}
            <Input value={newCard.label} onChange={e => setNewCard({ ...newCard, label: e.target.value })} placeholder="Label (e.g. Operating account)" className="h-11"/>
            {newCard.type !== "card" && (
              <div className="text-[10.5px] text-foreground/55">
                Need address / intermediary bank fields? <a href="/buyer/settings" className="underline">Use the full form in Settings → Payment & Billing</a>.
              </div>
            )}
          </div>
          <Button onClick={addCard} data-testid="card-save" className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
            Save method
          </Button>
        </DialogContent>
      </Dialog>

      {/* ───── Pay invoice — RIGHT-SIDE SHEET (Feb 2026 redesign) ─────
            Adaptive UX:
              • Inline by default — wallet, ACH, saved card, seller terms
                all process inside this panel without a modal.
              • Side-modal escape hatch — only the lender side / GetBalance / 3DS
                redirects need to take over the screen; they open in a
                child modal that overlays the sheet (sheet stays in DOM).
            • Desktop (≥640px): slides in from the RIGHT (520px wide)
            • Mobile (<640px):  slides up from the BOTTOM as a sheet that
              fills the bottom 90% of the viewport — much friendlier than
              a side-drawer on small screens.
            Triggered by ?pay=ID URL hash for deep-linking. */}
      <Sheet open={!!payInv} onOpenChange={(v) => {
        if (!v) { setPayInv(null); setPayState({ kind: "idle", message: null, details: null }); }
      }}>
        <SheetContent
          side={typeof window !== "undefined" && window.innerWidth < 640 ? "bottom" : "right"}
          className="w-full sm:max-w-[520px] overflow-y-auto p-0 sm:rounded-none rounded-t-2xl max-h-[92vh] sm:max-h-screen"
          data-testid="payment-panel"
        >
          <SheetCloseX/>
          <div className="p-6">
            <SheetHeader className="text-left">
              <SheetTitle className="display text-2xl tracking-tight">Authorize payment</SheetTitle>
              <SheetDescription className="text-[12px] text-foreground/55 sr-only">
                Pay this invoice via wallet, card, ACH, or BNPL.
              </SheetDescription>
            </SheetHeader>
          {payInvoice && stepupState === "awaiting_code" ? (
            <div className="py-6" data-testid="payment-stepup-otp">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 mb-4">
                <Shield className="w-3 h-3"/> Owner approval required
              </div>
              <h3 className="display text-xl tracking-tight text-foreground">Enter the code from the owner's phone.</h3>
              <p className="mt-2 text-[13px] text-foreground/65 leading-relaxed">
                We sent a 6-digit code to <strong className="text-foreground">{stepupCtx?.owner_name || "the account owner"}</strong>'s phone
                ({stepupCtx?.phone_masked}). Ask them for the code and enter it below.
              </p>
              {stepupCtx?.dev_mode && (
                <p className="mt-2 text-[11px] text-amber-700 italic">DEV mode: Twilio is not configured. Any 6-digit code works.</p>
              )}
              <div className="mt-5">
                <label className="text-[11.5px] text-foreground/65 font-medium block mb-1.5">6-digit code</label>
                <Input
                  value={stepupCode}
                  onChange={e => setStepupCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                  placeholder="123456"
                  inputMode="numeric" autoComplete="one-time-code"
                  data-testid="stepup-code-input"
                  className="bg-secondary/40 border-border text-center text-2xl tracking-[0.4em] font-mono"
                />
              </div>
              {stepupErr && (
                <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50/60 p-3 text-[12.5px] text-rose-900 flex gap-2 items-start">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
                  <div>{stepupErr}</div>
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  onClick={verifyStepupCode}
                  disabled={stepupState === "verifying" || stepupCode.length < 4}
                  className="flex-1 h-11 rounded-full bg-foreground text-background"
                  data-testid="stepup-verify-submit"
                >
                  {stepupState === "verifying"
                    ? <><RefreshCw className="w-4 h-4 animate-spin mr-2"/>Verifying…</>
                    : <>Verify &amp; pay {money(payInvoice.amount)}<ArrowRight className="w-4 h-4 ml-2"/></>}
                </Button>
                <Button variant="outline" onClick={() => {
                  setStepupState(null); setStepupCtx(null); setStepupCode("");
                }} className="h-11 rounded-full">Cancel</Button>
              </div>
              <div className="mt-3 text-center">
                <button type="button" onClick={requestStepupChallenge}
                        className="text-[11.5px] text-foreground/55 hover:text-foreground underline">
                  Didn't get the code? Resend
                </button>
              </div>
            </div>
          ) : payInvoice && payState.kind === "pending_approval" ? (
            <div className="py-8" data-testid="payment-pending-approval-state">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/30 inline-flex items-center justify-center mb-5">
                  <Shield className="w-8 h-8 text-amber-700" strokeWidth={2}/>
                </div>
                <div className="display text-2xl tracking-tight text-foreground">Awaiting co-approval</div>
                <div className="text-[10.5px] mono uppercase tracking-[0.2em] text-foreground/50 mt-3">
                  Invoice {payInvoice.code} · {money(payInvoice.amount)}
                </div>
                <p className="mt-4 max-w-[380px] text-[13px] text-foreground/65 leading-relaxed">
                  {payState.details}
                </p>
                <p className="mt-4 text-[11.5px] text-foreground/45">
                  Your wallet is held in escrow until {payState.approvers_pending} approver{payState.approvers_pending !== 1 ? 's' : ''} sign off.
                  You'll get a notification when this is approved or rejected.
                </p>
              </div>
            </div>
          ) : payInvoice && payState.kind === "success" ? (
            <PaymentSuccessWithUndo
              invoice={payInvoice}
              payState={payState}
              money={money}
              onUndo={async () => {
                try {
                  await api.post(`/invoices/${payInv}/undo-recent-payment`);
                  setPayState({ kind: "undone",
                    message: "Payment reversed",
                    details: "Your wallet has been credited back. The invoice is now unpaid." });
                  setTimeout(() => {
                    setPayInv(null);
                    setPayState({ kind: "idle", message: null, details: null });
                    load();
                  }, 3000);
                } catch (e) {
                  toast.error(e?.response?.data?.detail?.message
                            || e?.response?.data?.detail
                            || "Couldn't undo — window may have expired.");
                }
              }}
              onTimerExpire={() => {
                setPayInv(null);
                setApprovalToken(null);
                setPayState({ kind: "idle", message: null, details: null });
                nav("/buyer/orders#journey", { replace: true });
                load();
              }}
            />
          ) : payInvoice && payState.kind === "undone" ? (
            <div className="py-8" data-testid="payment-undone-state">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/30 inline-flex items-center justify-center mb-5">
                  <RotateCcw className="w-8 h-8 text-amber-700" strokeWidth={2}/>
                </div>
                <div className="display text-2xl tracking-tight text-foreground">{payState.message}</div>
                <div className="text-[10.5px] mono uppercase tracking-[0.2em] text-foreground/50 mt-3">
                  Invoice {payInvoice.code} · {money(payInvoice.amount)}
                </div>
                {payState.details && (
                  <p className="mt-4 max-w-[360px] text-[13px] text-foreground/65 leading-relaxed">
                    {payState.details}
                  </p>
                )}
              </div>
            </div>
          ) : payInvoice ? (
            <>
              {payState.kind === "error" && (
                <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50/60 p-3 text-[12.5px] text-rose-900 flex gap-2 items-start" data-testid="payment-error-state">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
                  <div>
                    <div className="font-medium">{payState.message}</div>
                    <button onClick={() => setPayState({ kind: "idle", message: null, details: null })}
                            className="mt-1.5 text-rose-700 underline text-[12px]">
                      Choose a different method &amp; retry
                    </button>
                  </div>
                </div>
              )}
              <div className="mt-2">
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50">Invoice {payInvoice.code} · {payInvoice.seller_name}</div>
                <div className="display text-5xl mt-2 tracking-[-0.02em]">{money(payInvoice.amount)}</div>
                <div className="text-xs text-foreground/55 mt-1">Due {new Date(payInvoice.due_date).toLocaleDateString()} · {payInvoice.terms?.toUpperCase()}</div>
                {payInvoice.expected_ready_at && (
                  <div className="mt-3 inline-flex items-center gap-2 chip bg-blue-100 text-blue-800 border border-blue-200" data-testid="ready-by-chip">
                    <Building2 className="w-3 h-3"/>Seller will have order ready to ship by {new Date(payInvoice.expected_ready_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                )}
              </div>
              {/* ─── Seller-issued free terms (auto-detected) ─── */}
              {/* Mode A · click to select; click again to deselect (toggle).
                  When selected, ALL other sections (Choose action / Pay
                  later / Prepay with) are hidden — picking free supplier
                  terms is a terminal choice. */}
              {eligibleSellerOffer && (
                <div className="mt-5" data-testid="seller-terms-section">
                  <div className="text-[10px] mono uppercase tracking-[0.2em] text-emerald-700 mb-2 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3"/>
                    Free terms from your supplier
                  </div>
                  <button
                    onClick={() => {
                      if (payMethod === "seller_terms") {
                        // Toggle off → revert to Mode B (Prepay · wallet).
                        setPayMethod("wallet"); setPayMode("now"); setExtension(null);
                      } else {
                        setPayMethod("seller_terms"); setSellerOfferId(eligibleSellerOffer.id);
                      }
                    }}
                    data-testid="paym-seller-terms"
                    className={`w-full text-left rounded-xl border p-4 transition-colors ${payMethod === "seller_terms" ? "border-emerald-500 bg-emerald-50/40" : "border-emerald-200 bg-emerald-50/20 hover:border-emerald-400"}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0">
                        <Shield className="w-4 h-4"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">
                          Free {eligibleSellerOffer.terms.toUpperCase()} from {eligibleSellerOffer.seller_name}
                        </div>
                        <div className="text-[11px] text-foreground/60 mt-0.5">
                          No fee · no application · settles on {new Date(Date.now() + (eligibleSellerOffer.terms === "net14" ? 14 : 30) * 86400000).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </div>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex-1 h-1.5 rounded-full bg-emerald-100 overflow-hidden">
                            <div
                              className="h-full bg-emerald-500"
                              style={{ width: `${Math.min(100, eligibleSellerOffer.utilization_pct || 0)}%` }}
                            />
                          </div>
                          <div className="text-[10px] mono text-foreground/55 shrink-0">
                            {money(eligibleSellerOffer.used)} used · {money(eligibleSellerOffer.available)} left
                          </div>
                        </div>
                      </div>
                      {payMethod === "seller_terms" && <Check className="w-4 h-4 text-emerald-700 mt-1"/>}
                    </div>
                  </button>

                  {/* Buyer-paid extension on top of free terms */}
                  {payMethod === "seller_terms" && cap.credit_limit > 0 && (
                    <div className="mt-3" data-testid="extension-section">
                      <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50 mb-2">
                        Need more time? Extend via the lender
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {[
                          { k: null, l: `Free ${eligibleSellerOffer.terms.toUpperCase()}`, sub: "$0 fee" },
                          { k: "net60", l: "Net-60", sub: `+${money(payInvoice.amount * 0.001 * (60 - (eligibleSellerOffer.terms === "net14" ? 14 : 30)))} fee` },
                          { k: "net90", l: "Net-90", sub: `+${money(payInvoice.amount * 0.001 * (90 - (eligibleSellerOffer.terms === "net14" ? 14 : 30)))} fee` },
                        ].map(opt => (
                          <button
                            key={opt.k || "free"}
                            onClick={() => setExtension(opt.k)}
                            data-testid={`ext-${opt.k || "free"}`}
                            className={`text-left rounded-xl border p-2.5 transition-colors ${extension === opt.k ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}
                          >
                            <div className="text-xs font-medium">{opt.l}</div>
                            <div className="text-[10px] text-foreground/55 mt-0.5 mono">{opt.sub}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Mode toggle — Mode B (Prepay · wallet only) vs Mode C
                  (Authorize · lender-financed net terms). Hidden entirely
                  when Mode A (free seller terms) is selected, since
                  picking free terms is a terminal choice.
                  Feb 2026 · this single picker decides which payment
                  section renders below (#3 Pay later OR #4 Prepay with). */}
              {payInvoice.terms !== "prepaid" && payMethod !== "seller_terms" && (
                <div className="mt-5">
                  <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50 mb-2">Choose action</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={() => { setPayMode("now"); setPayMethod("wallet"); }}
                      data-testid="mode-now"
                      className={`text-left rounded-xl border p-3 transition-colors ${payMode === "now" && payMethod !== "two_inc" ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}>
                      <div className="text-sm font-medium">Prepay upfront</div>
                      <div className="text-[11px] text-foreground/55 mt-0.5">Pay now from your wallet · instant · no fee</div>
                    </button>
                    <button
                      onClick={() => {
                        // Mode C — pick a lender-financed net term so the
                        // seller is paid today, the buyer pays the lender
                        // on the chosen due date. selectedTwoTerm picker
                        // appears in Section 3 below.
                        setPayMode("authorize");
                        setPayMethod("two_inc");
                        if (!selectedTwoTerm) setSelectedTwoTerm("net30");
                      }}
                      data-testid="mode-authorize"
                      className={`text-left rounded-xl border p-3 transition-colors ${payMethod === "two_inc" ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/30"}`}>
                      <div className="text-sm font-medium">Authorize · lender net terms</div>
                      <div className="text-[11px] text-foreground/55 mt-0.5">Seller paid today · you pay the lender later</div>
                    </button>
                  </div>
                </div>
              )}

              {/* Section 3 · Mode C only — lender-financed net term picker.
                  Hidden under Mode A (seller_terms) and Mode B (prepay). */}
              {payInvoice.terms !== "prepaid" && payMethod === "two_inc" && (
                <div className="mt-5" data-testid="two-net-terms-section">
                  <div className="text-[10px] mono uppercase tracking-[0.2em] text-blue-700 mb-2 inline-flex items-center gap-1.5">
                    <Shield className="w-3 h-3"/>Pay later · financed by the lender
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setPayMethod("two_inc")}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setPayMethod("two_inc"); }}
                    data-testid="paym-two-inc"
                    className={`w-full text-left rounded-xl border p-4 transition-colors cursor-pointer ${payMethod === "two_inc" ? "border-blue-500 bg-blue-50/40" : "border-blue-200 bg-blue-50/20 hover:border-blue-400"}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-600 text-white grid place-items-center shrink-0 display text-xs">2.</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">Pay in {(twoTerms.find(t => t.code === selectedTwoTerm) || {}).label || "Net-30"} · seller paid today</div>
                        <div className="text-[11px] text-foreground/60 mt-0.5">
                          {(() => {
                            const term = twoTerms.find(t => t.code === selectedTwoTerm) || { days: 30, fee_pct: 0 };
                            const due = new Date(Date.now() + term.days * 86400000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
                            return `Authorize once · pay the lender on ${due} · ${term.fee_pct > 0 ? `${term.fee_pct}% buyer fee` : "0% fee"}`;
                          })()}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(twoTerms.length > 0 ? twoTerms : [{code:"net30",label:"Net-30",days:30}]).map(t => (
                            <button
                              key={t.code}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setSelectedTwoTerm(t.code); setPayMethod("two_inc"); }}
                              data-testid={`two-term-pill-${t.code}`}
                              className={`text-[10px] mono px-2 py-1 rounded-full border transition-colors ${selectedTwoTerm === t.code && payMethod === "two_inc" ? "border-blue-600 bg-blue-600 text-white" : "border-blue-300 text-blue-800 hover:bg-blue-100"}`}
                            >
                              {t.label}{t.fee_pct > 0 ? ` · ${t.fee_pct}%` : ""}
                            </button>
                          ))}
                        </div>
                      </div>
                      {payMethod === "two_inc" && <Check className="w-4 h-4 text-blue-700 mt-1"/>}
                    </div>
                  </div>
                </div>
              )}

              {/* Section 4 · Mode B only — wallet-only prepay.
                  Hidden under Mode A (seller_terms) and Mode C (two_inc).
                  When wallet balance < invoice amount, swap the wallet
                  tile for a "Top up wallet first" CTA that opens the
                  Add-funds dialog (ACH/wire/card top-up paths live there
                  — not duplicated inside this drawer per UX spec). */}
              {payInvoice.terms !== "prepaid" && payMode === "now" && payMethod !== "two_inc" && payMethod !== "seller_terms" && (
                <div className="mt-5">
                  <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/50 mb-2">Prepay with</div>
                  {wallet.balance >= payInvoice.amount ? (
                    <button
                      onClick={() => setPayMethod("wallet")}
                      data-testid="paym-wallet"
                      className={`w-full text-left flex items-center gap-3 py-3 px-3 rounded-xl border transition-colors ${payMethod === "wallet" ? "border-foreground bg-foreground/[0.03]" : "border-border hover:border-foreground/40"}`}>
                      <Wallet className="w-4 h-4"/>
                      <div className="flex-1">
                        <div className="text-sm font-medium">Wallet balance</div>
                        <div className="text-[11px] text-foreground/55">{money(wallet.balance)} available · instant · no fee</div>
                      </div>
                      {payMethod === "wallet" && <Check className="w-4 h-4"/>}
                    </button>
                  ) : (
                    <div
                      className="rounded-xl border border-amber-200 bg-amber-50/40 p-3.5 flex items-start gap-3"
                      data-testid="wallet-insufficient-cta"
                    >
                      <Wallet className="w-4 h-4 text-amber-700 mt-0.5 shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-amber-900">Top up your wallet first</div>
                        <div className="text-[11px] text-amber-800/80 mt-0.5">
                          You have {money(wallet.balance)} · need {money(payInvoice.amount - wallet.balance)} more to prepay.
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          // Iter 156 · pre-fill the topup with the EXACT
                          // shortfall and pin the pending invoice — the
                          // webhook auto-pays this invoice the moment the
                          // topup clears, so the buyer never has to come
                          // back and re-click Pay.
                          const shortfall = Math.max(0, payInvoice.amount - wallet.balance);
                          setTopupAmount(Math.ceil(shortfall * 100) / 100);
                          setTopupPendingInvoice({
                            id:         payInvoice.id,
                            code:       payInvoice.code,
                            amount:     payInvoice.amount,
                            shortfall:  shortfall,
                            sellerName: payInvoice.seller_name,
                          });
                          setTopupSource("rfp");  // fastest rail by default
                          setPayInv(null);
                          setTopup(true);
                        }}
                        data-testid="topup-from-drawer"
                        className="rounded-full h-8 px-3 bg-amber-700 hover:bg-amber-800 text-white"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1"/>Add funds
                      </Button>
                    </div>
                  )}
                  {eligibleSellerOffer && (
                    <button
                      onClick={() => { setPayMethod("seller_terms"); setSellerOfferId(eligibleSellerOffer.id); }}
                      className="mt-2 text-[11px] text-emerald-700 hover:underline"
                      data-testid="back-to-seller-terms"
                    >
                      ← Back to free {eligibleSellerOffer.terms.toUpperCase()} from {eligibleSellerOffer.seller_name}
                    </button>
                  )}
                </div>
              )}
              <Button
                onClick={async () => {
                  if (payMethod === "two_inc") {
                    startTwoCheckout(payInv, selectedTwoTerm);
                    return;
                  }
                  // Feb 2026 — every payment requires SMS step-up to the
                  // OWNER's phone. Trigger the challenge first, render
                  // the OTP screen, then proceed once the user enters the
                  // code.
                  await requestStepupChallenge();
                }}
                disabled={twoStarting || payState.kind === "processing" || stepupState === "challenging" || (payMethod === "wallet" && wallet.balance < payInvoice.amount)}
                data-testid="authorize-pay"
                className="mt-5 w-full h-12 rounded-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60"
              >
                {payState.kind === "processing"
                  ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin"/>{
                      payMethod === "wallet" ? "Settling from wallet…" :
                      payMethod === "ach" ? "Submitting ACH…" :
                      payMethod === "card" ? "Charging your card…" :
                      payMethod === "wire" ? "Recording wire intent…" :
                      payMethod === "two_inc" ? "Authorizing with the lender…" :
                      payMethod === "seller_terms" ? "Authorizing terms…" :
                      "Processing payment…"
                    }</>
                  : stepupState === "challenging"
                    ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin"/>Sending approval code to owner…</>
                  : payMethod === "two_inc"
                  ? <>Authorize · {(twoTerms.find(t => t.code === selectedTwoTerm) || {}).label || "Net-30"} · {money(payInvoice.amount)}</>
                  : payMethod === "seller_terms"
                    ? <>Authorize via free {(extension || eligibleSellerOffer?.terms || "").toUpperCase()} · {money(payInvoice.amount)}</>
                    : <>Pay {money(payInvoice.amount)} from wallet</>}
                {payState.kind !== "processing" && stepupState !== "challenging" && <ArrowRight className="w-4 h-4 ml-1.5"/>}
              </Button>
              <div className="mt-2 text-center text-[10px] text-foreground/45 inline-flex items-center justify-center gap-2 w-full">
                <Lock className="w-2.5 h-2.5"/>SSL · PCI L1 · {payMethod === "two_inc" ? "the lender underwrites & pays the seller today · you pay the lender on due date" : payMethod === "seller_terms" ? `Settles on supplier's ${(extension || eligibleSellerOffer?.terms || "").toUpperCase()} due date` : "No real funds moved"}
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-foreground/50 text-sm">Loading invoice…</div>
          )}
          </div>
        </SheetContent>
      </Sheet>

      {/* ───── Apply dialog ───── */}
      <Dialog open={!!applyOpen} onOpenChange={(v) => !v && setApplyOpen(null)}>
        <DialogContent className="max-w-md">
          {applyOpen && (
            <>
              <DialogHeader><DialogTitle className="display text-2xl tracking-tight">{applyOpen.isTwo ? "Request credit via the lender" : `Apply to ${applyOpen.name}`}</DialogTitle></DialogHeader>
              <div className="text-sm text-foreground/60 mt-1">
                {applyOpen.isTwo
                  ? "We'll send your business profile to the lender for an instant credit assessment. Soft check only — no impact to your business credit score."
                  : `Your Verqo Trade Score and last 12 months of trade data will be shared with ${applyOpen.name}. No hard credit pull.`}
              </div>
              <div className="mt-5 space-y-3">
                <div>
                  <Label>Amount</Label>
                  <Input id="apply-amount" type="number" defaultValue={50000} className="mt-1.5 h-11" data-testid="apply-amount"/>
                  <div className="text-[11px] text-foreground/55 mt-1.5 inline-flex items-center gap-1.5">
                    <FileText className="w-3 h-3"/>Credit will be used to pay invoices on the platform
                  </div>
                </div>
              </div>
              <Button
                onClick={async () => {
                  if (applyOpen.isTwo) {
                    const amt = Number(document.getElementById("apply-amount")?.value || 50000);
                    setApplyOpen(null);
                    await requestTwoCreditCheck(amt);
                  } else {
                    setApplyOpen(null);
                    toast.success(`Application sent · decision in ${applyOpen.speed || "30s"}`);
                  }
                }}
                className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90" data-testid="apply-confirm">
                {applyOpen.isTwo ? "Run the lender credit check" : "Submit application"} <ArrowRight className="w-4 h-4 ml-1.5"/>
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ───── the lender embedded checkout ───── */}
      <TwoCheckoutModal
        open={!!twoCheckout}
        onClose={() => setTwoCheckout(null)}
        paymentUrl={twoCheckout?.paymentUrl}
        titleAmount={twoCheckout?.amount}
        onSuccess={onTwoSuccess}
        onCancel={onTwoCancel}
      />

      {/* ───── the lender one-time business-profile mini-form ───── */}
      <Dialog open={twoProfileOpen} onOpenChange={(v) => !v && setTwoProfileOpen(false)}>
        <DialogContent className="max-w-md" data-testid="two-profile-dialog">
          <DialogHeader>
            <DialogTitle className="display text-2xl tracking-tight">Confirm business details</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-foreground/60 mt-1">
            the lender needs these once to underwrite your credit line. We'll save them so you never have to re-enter.
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Legal company name</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.company_name}
                onChange={e => setTwoProfileForm(f => ({ ...f, company_name: e.target.value }))}
                data-testid="two-profile-company-name"/>
            </div>
            <div>
              <Label>Tax ID / EIN / Reg #</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.organization_number}
                onChange={e => setTwoProfileForm(f => ({ ...f, organization_number: e.target.value }))}
                data-testid="two-profile-ein"/>
            </div>
            <div>
              <Label>Country</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.country}
                onChange={e => setTwoProfileForm(f => ({ ...f, country: e.target.value.toUpperCase().slice(0,2) }))}
                placeholder="US"/>
            </div>
            <div>
              <Label>First name</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.first_name}
                onChange={e => setTwoProfileForm(f => ({ ...f, first_name: e.target.value }))}/>
            </div>
            <div>
              <Label>Last name</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.last_name}
                onChange={e => setTwoProfileForm(f => ({ ...f, last_name: e.target.value }))}/>
            </div>
            <div className="col-span-2">
              <Label>Phone</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.phone}
                onChange={e => setTwoProfileForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+1 555 123 4567"/>
            </div>
            <div className="col-span-2">
              <Label>Street address</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.billing_address.street_address}
                onChange={e => setTwoProfileForm(f => ({ ...f, billing_address: { ...f.billing_address, street_address: e.target.value }}))}/>
            </div>
            <div>
              <Label>City</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.billing_address.city}
                onChange={e => setTwoProfileForm(f => ({ ...f, billing_address: { ...f.billing_address, city: e.target.value }}))}/>
            </div>
            <div>
              <Label>State / Region</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.billing_address.region || ""}
                onChange={e => setTwoProfileForm(f => ({ ...f, billing_address: { ...f.billing_address, region: e.target.value }}))}/>
            </div>
            <div>
              <Label>Postal code</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.billing_address.postal_code}
                onChange={e => setTwoProfileForm(f => ({ ...f, billing_address: { ...f.billing_address, postal_code: e.target.value }}))}/>
            </div>
            <div>
              <Label>Country (addr.)</Label>
              <Input className="mt-1.5 h-11" value={twoProfileForm.billing_address.country}
                onChange={e => setTwoProfileForm(f => ({ ...f, billing_address: { ...f.billing_address, country: e.target.value.toUpperCase().slice(0,2) }}))}
                placeholder="US"/>
            </div>
          </div>
          <Button onClick={saveTwoProfile} data-testid="two-profile-save"
            className="mt-5 w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90">
            Save &amp; continue <ArrowRight className="w-4 h-4 ml-1.5"/>
          </Button>
          <div className="text-[10px] text-center text-foreground/45 mt-2 inline-flex items-center justify-center gap-1.5 w-full">
            <Shield className="w-2.5 h-2.5"/>Soft credit check only · no impact to credit score
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase 2c — Paid-history invoice detail dialog. Lets a buyer re-open a
       *  paid invoice and see the items + shipping breakdown for reconciliation. */}
      <BuyerInvoiceDetailDialog invoice={paidViewInvoice} onClose={() => setPaidViewInvoice(null)} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers — small presentational components
// ─────────────────────────────────────────────────────────────────────────────

function FlowTile({ icon: Icon, label, value, hint, tone, testid }) {
  const positive = tone === "positive";
  return (
    <div className="bg-background p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] mono uppercase tracking-[0.15em] text-foreground/50">{label}</div>
        <div className={`w-7 h-7 rounded-full grid place-items-center ${positive ? "bg-accent/30" : "bg-secondary"}`}>
          <Icon className="w-3.5 h-3.5"/>
        </div>
      </div>
      <div className="display text-3xl mt-3 tracking-tight">{value}</div>
      <div className="text-[11px] text-foreground/50 mt-1">{hint}</div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, href, testid }) {
  const inner = (
    <div className="rounded-2xl border border-border bg-white px-5 py-5 hover:border-foreground/40 hover:bg-secondary/40 transition-all flex items-center gap-3 group cursor-pointer" data-testid={testid}>
      <div className="w-10 h-10 rounded-xl bg-foreground text-accent grid place-items-center"><Icon className="w-4 h-4"/></div>
      <div className="flex-1 text-sm font-medium">{label}</div>
      <ArrowRight className="w-4 h-4 text-foreground/40 group-hover:text-foreground group-hover:translate-x-0.5 transition-all"/>
    </div>
  );
  if (href) return <a href={href}>{inner}</a>;
  return <button onClick={onClick} className="text-left w-full">{inner}</button>;
}

function ActivityList({ items }) {
  if (!items?.length) return <div className="py-12 text-center text-foreground/40 text-sm">No activity yet</div>;
  return (
    <div className="divide-y divide-border border-y border-border">
      {items.map((e, i) => {
        const isOut = e.amount < 0 || e.kind === "out";
        return (
          <div key={i} className="py-4 grid grid-cols-12 gap-3 items-center text-sm">
            <div className="col-span-1">
              <div className={`w-9 h-9 rounded-full grid place-items-center ${isOut ? "bg-secondary" : "bg-accent/20"}`}>
                {isOut ? <ArrowUpRight className="w-3.5 h-3.5"/> : <ArrowDownLeft className="w-3.5 h-3.5"/>}
              </div>
            </div>
            <div className="col-span-7">
              <div className="font-medium">{e.title}</div>
              <div className="text-[11px] text-foreground/50 mono mt-0.5">{e.sub}</div>
            </div>
            <div className="col-span-2 text-xs text-foreground/55">{new Date(e.date).toLocaleDateString()}</div>
            <div className={`col-span-2 text-right display ${isOut ? "text-foreground/65" : "text-emerald-700"}`}>
              {isOut ? "-" : "+"}{money(Math.abs(e.amount))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function buildActivity(invoices, wallet) {
  const a = [];
  invoices.slice(0, 50).forEach(i => {
    if (i.status === "paid") {
      a.push({ kind: "out", title: `Paid ${i.code}`, sub: `${i.seller_name} · ${i.terms?.toUpperCase()}`, amount: -i.amount, date: i.created_at });
    } else {
      a.push({ kind: "out", title: `Invoice ${i.code} issued`, sub: `${i.seller_name} · due ${new Date(i.due_date).toLocaleDateString()}`, amount: -i.amount, date: i.created_at });
    }
  });
  (wallet?.transactions || []).forEach(tx => {
    a.push({
      kind: tx.amount > 0 ? "in" : "out",
      title: tx.type === "topup" ? "Wallet top-up" : "Wallet payment",
      sub: `${tx.method?.toUpperCase()} · ${tx.ref}`,
      amount: tx.amount, date: tx.created_at,
    });
  });
  return a.sort((x, y) => new Date(y.date) - new Date(x.date));
}


// ─────────────────────────────────────────────────────────────────────────────
// OpenBalancesList — manual pay-down for unpaid invoices + open credit draws
// ─────────────────────────────────────────────────────────────────────────────
function OpenBalancesList({ invoices, outstanding, rate, onPay, onPayDraw }) {
  const [drawAmt, setDrawAmt] = useState("");
  // Phase 2c — detail dialog state (shows the items vs shipping
  // breakdown when shipping_passthrough is stamped on the invoice).
  const [viewInvoice, setViewInvoice] = useState(null);
  const open = invoices
    .filter(i => ["sent", "outstanding", "overdue"].includes(i.status))
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const daysUntil = (d) => Math.ceil((new Date(d) - Date.now()) / 86400000);
  const hasNothing = open.length === 0 && outstanding === 0;

  if (hasNothing) {
    return (
      <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="balances-empty">
        {/* Sample preview — shows what the open-balances list will look like
         *  once the buyer has placed orders + received invoices. Renders only
         *  in true empty state (no invoices, no outstanding credit). */}
        <SampleBuyerInvoicesPreview />
        <div className="p-6 border-t border-border text-center text-xs text-foreground/55">
          All clear — no real open invoices or outstanding credit draws.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="open-balances-list">
      {/* Open invoices */}
      {open.length > 0 && (
        <>
          <div className="px-5 py-3 text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 bg-secondary/40 border-b border-border flex justify-between">
            <span>Open invoices · {open.length}</span>
            <span>{money(open.reduce((s,i)=>s+i.amount,0))} due</span>
          </div>
          <div className="divide-y divide-border">
            {open.slice(0, 6).map(i => {
              const d = daysUntil(i.due_date);
              const overdue = d < 0;
              const due_soon = d >= 0 && d <= 7;
              return (
                <div key={i.id} className="grid grid-cols-12 gap-3 items-center px-5 py-4" data-testid={`balance-inv-${i.code}`}>
                  <div className="col-span-1">
                    <div className="w-9 h-9 rounded-xl bg-secondary grid place-items-center"><FileText className="w-4 h-4"/></div>
                  </div>
                  <div className="col-span-3">
                    <div className="font-medium text-sm">{i.code}</div>
                    <div className="text-[11px] mono text-foreground/55 mt-0.5">{i.terms?.toUpperCase()} · {i.seller_name}</div>
                  </div>
                  <div className="col-span-3 text-xs">
                    <div className="text-foreground/65">Due {new Date(i.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                    <div className={`text-[10px] mt-0.5 ${overdue ? "text-rose-700 font-medium" : due_soon ? "text-amber-700" : "text-foreground/45"}`}>
                      {overdue ? `${Math.abs(d)} days overdue` : d === 0 ? "Due today" : `In ${d} day${d === 1 ? "" : "s"}`}
                    </div>
                  </div>
                  <div className="col-span-2 text-right">
                    <div className="display text-base">{money(i.amount)}</div>
                    {/* Phase IV — Validity countdown shown directly next to the
                     *  amount so the buyer can't miss it. Anchors the chip to
                     *  the right side, same column as the price, with stronger
                     *  visual weight than tucking it under the due-date column. */}
                    <div className="mt-1 inline-flex">
                      <InvoiceExpiryChip invoice={i} compact />
                    </div>
                    {i.shipping_passthrough && Number(i.shipping_passthrough.billed) > 0 && (
                      <button
                        type="button"
                        onClick={() => setViewInvoice(i)}
                        data-testid={`shipping-subtext-${i.code}`}
                        className="mt-1 block text-[10.5px] text-foreground/55 hover:text-foreground hover:underline"
                      >
                        incl. {money(i.shipping_passthrough.billed)} shipping
                      </button>
                    )}
                  </div>
                  <div className="col-span-3 flex justify-end gap-1.5">
                    <Button size="sm" variant="ghost"
                            onClick={() => setViewInvoice(i)}
                            data-testid={`view-invoice-${i.code}`}
                            className="h-8 px-2.5 text-foreground/55 hover:text-foreground">
                      View
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onPay(i.id)} className="rounded-full h-8 px-3" data-testid={`pay-later-${i.code}`}>
                      Pay later
                    </Button>
                    <Button size="sm" onClick={() => onPay(i.id)} data-testid={`pay-now-${i.code}`}
                      className="rounded-full h-8 px-3 bg-foreground text-background hover:bg-foreground/90">
                      Pay now <ArrowRight className="w-3 h-3 ml-1"/>
                    </Button>
                  </div>
                </div>
              );
            })}
            {open.length > 6 && (
              <div className="px-5 py-3 text-center text-xs text-foreground/55">
                + {open.length - 6} more · <a href="/buyer/orders#action" className="text-foreground hover:underline">view all open invoices</a>
              </div>
            )}
          </div>
        </>
      )}

      {/* Credit draw balance */}
      {outstanding > 0 && (
        <>
          <div className="px-5 py-3 text-[10px] mono uppercase tracking-[0.15em] text-foreground/50 bg-secondary/40 border-y border-border flex justify-between">
            <span>Credit line · open draw</span>
            <span>Rate {(rate * 100).toFixed(2)}% APR</span>
          </div>
          <div className="grid grid-cols-12 gap-3 items-center px-5 py-5" data-testid="balance-draw">
            <div className="col-span-1"><div className="w-9 h-9 rounded-xl bg-foreground text-accent grid place-items-center"><Landmark className="w-4 h-4"/></div></div>
            <div className="col-span-3">
              <div className="font-medium text-sm">the lender credit draw</div>
              <div className="text-[11px] mono text-foreground/55 mt-0.5">Revolving · interest accrues daily</div>
            </div>
            <div className="col-span-3 text-xs">
              <div className="text-foreground/65">Min payment due</div>
              <div className="text-[10px] mt-0.5 text-foreground/45">2% · {money(outstanding * 0.02)} this cycle</div>
            </div>
            <div className="col-span-2 text-right">
              <div className="display text-base">{money(outstanding)}</div>
              <div className="text-[10px] text-foreground/45">balance</div>
            </div>
            <div className="col-span-3 flex justify-end gap-1.5">
              <Input type="number" placeholder="Amount" value={drawAmt} onChange={e=>setDrawAmt(e.target.value)}
                className="h-8 w-24 text-sm" data-testid="draw-amount"/>
              <Button size="sm" onClick={() => { onPayDraw(drawAmt); setDrawAmt(""); }} data-testid="pay-draw"
                className="rounded-full h-8 px-3 bg-foreground text-background hover:bg-foreground/90">
                Pay
              </Button>
            </div>
          </div>
          <div className="px-5 pb-4 flex flex-wrap gap-1.5">
            {[outstanding * 0.02, outstanding * 0.25, outstanding * 0.5, outstanding].map((amt, i) => (
              <button key={i} onClick={() => setDrawAmt(Math.round(amt).toString())} className="text-[11px] rounded-full border border-border px-2.5 py-1 hover:border-foreground/40">
                {i === 0 ? "Min" : i === 3 ? "Pay in full" : `${[25,50][i-1]}%`} · {money(Math.round(amt))}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Phase 2c — invoice detail dialog with shipping breakdown */}
      <BuyerInvoiceDetailDialog invoice={viewInvoice} onClose={() => setViewInvoice(null)} />
    </div>
  );
}

// ── BuyerMethodRow ────────────────────────────────────────────────────────
// Single row in the Methods list. Banks gain a "Wire & ACH details" toggle
// that opens the full BankInstructionsCard (Mercury-style tabbed
// instructions). Cards stay PAN-locked — we never expose more than last4
// (PCI-DSS scope).
function BuyerMethodRow({ m, onSetDefault }) {
  const [open, setOpen] = useState(false);
  const isBank = m.type === "bank";
  const rails = (m.rails || "").split(",").map(s => s.trim()).filter(Boolean);
  const hasInstructions = isBank && (rails.length > 0 || m.iban || m.swift_bic);

  return (
    <div className="py-5 border-y border-border -mx-4 px-4 hover:bg-secondary/30 rounded-md transition-colors" data-testid={`method-${m.id}`}>
      <div className="grid grid-cols-12 items-center gap-4">
        <div className="col-span-1">
          <div className={`w-12 h-8 rounded-md grid place-items-center text-[10px] font-bold text-white ${m.brand === "visa" ? "bg-blue-700" : m.brand === "mastercard" ? "bg-orange-600" : m.brand === "amex" ? "bg-emerald-700" : "bg-foreground"}`}>
            {isBank ? "BANK" : (m.brand || "CARD").toUpperCase().slice(0, 4)}
          </div>
        </div>
        <div className="col-span-5">
          <div className="font-medium">{m.label || `${m.brand?.toUpperCase()} •••• ${m.last4}`}</div>
          <div className="text-xs text-foreground/55 mt-0.5">
            {!isBank ? `Card · exp ${m.exp || "—"}` : `Bank · ${m.bank_name || "ACH"}`}
            {" · added "}{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
          </div>
        </div>
        <div className="col-span-2 text-xs text-foreground/55 font-mono">•••• {m.last4}</div>
        <div className="col-span-2 flex items-center gap-2">
          {hasInstructions && (
            <button
              onClick={() => setOpen(o => !o)}
              data-testid={`method-toggle-${m.id}`}
              className="text-xs text-foreground/70 hover:text-foreground underline-offset-2 hover:underline"
            >
              {open ? "Hide instructions" : "Wire & ACH details"}
            </button>
          )}
          {m.is_default
            ? <span className="chip bg-accent/30 border border-accent/40 text-[10px]">Default</span>
            : <button onClick={onSetDefault} className="text-xs text-foreground/60 hover:text-foreground" data-testid={`set-default-${m.id}`}>Set default</button>}
        </div>
        <div className="col-span-2 text-right">
          <button className="text-xs text-foreground/50 hover:text-rose-700">Remove</button>
        </div>
      </div>
      {open && hasInstructions && (
        <div className="mt-4 ml-[calc((100%/12)+1rem)] pt-4 border-t border-border" data-testid={`method-reveal-${m.id}`}>
          <BankInstructionsCard method={m} testIdRoot={`method-${m.id}-instr`}/>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SampleBuyerInvoicesPreview — empty-state preview for BuyerPayments
// ─────────────────────────────────────────────────────────────────────────────
function SampleBuyerInvoicesPreview() {
  const [sampleInv, setSampleInv] = useState(null);
  return (
    <div data-testid="sample-buyer-invoices-preview">
      <SamplePreviewBanner noun="Invoices"/>
      {SAMPLE_INVOICES.map(inv => {
        const tone =
          inv.status === "paid"     ? "bg-emerald-500/15 text-emerald-700" :
          inv.status === "overdue"  ? "bg-rose-500/15 text-rose-700" :
                                       "bg-amber-500/15 text-amber-800";
        return (
          <button
            type="button"
            key={inv.id}
            onClick={() => setSampleInv(inv)}
            data-testid={`sample-buyer-invoice-row-${inv.id}`}
            className="w-full grid grid-cols-[1fr_1.4fr_120px_110px_140px_120px] gap-3 px-5 py-3 items-center border-b border-border opacity-[0.78] hover:opacity-100 hover:bg-secondary/40 transition-all"
          >
            <div className="font-mono text-[12.5px] text-slate-700 flex items-center gap-1.5">
              {inv.code}
              <SampleChip />
            </div>
            <div className="text-[13px] text-slate-700 truncate">{inv.seller_name}</div>
            <div>
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${tone}`}>
                {inv.status}
              </span>
            </div>
            <div className="text-[12px] text-slate-600 uppercase">{inv.terms.replace(/_/g, "-")}</div>
            <div className="text-[13px] font-medium text-slate-700 tabular-nums">${inv.amount.toLocaleString()}</div>
            <div className="text-right text-[11px] text-foreground/40">Open →</div>
          </button>
        );
      })}
      <SampleInvoiceDialog invoice={sampleInv} onClose={() => setSampleInv(null)} />
    </div>
  );
}

